import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, CircularProgress, Alert, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, FormControl, InputLabel, MenuItem,
  Menu, Divider, Slider,
} from '@mui/material';
import {
  listPlaybookFormations, createPlaybookPlay, updatePlaybookPlay,
} from '../../services/playbookService';
import {
  structureTermLabel, formatPlayersPerSide, resolvePlayersPerSide, playersPerSideToChoice,
  emptyPlayDocument, ensurePostSnap, ensurePreSnap, ensureWidePlayFieldConfig,
  opponentUnitTypeFor, snapshotMarkers,
} from '../../utils/playbook';
import { startersForUnitType } from '../../utils/playbookTemplates';
import { newMarkerId, resolveMarkerCollisions } from '../../utils/playbookMarkers';
import {
  ensureTimeline, sortedKeyframes, addKeyframeAtPosition, renameKeyframe, deleteKeyframe,
  findKeyframeAtPosition, bracketKeyframesForPosition, getActorKeyframeState,
  setActorKeyframeProgress, clearActorKeyframeState, pruneTimelineActors, pruneMomentCues,
  isPathBasedTimelineType, pointAlongPolyline, getMomentCues, replaceKeyframeCues,
} from '../../utils/playbookTimeline';
import {
  FORMATION_FULL_SCRUB, SNAP_FULL_SCRUB, END_FULL_SCRUB,
  fullScrubToLifecycle, lifecycleToFullScrub, isAtFormation, isAtSnap, keyframePhase,
  advanceFullScrub, PRE_SNAP_PREVIEW_DURATION_MS, POST_SNAP_PREVIEW_DURATION_MS,
  isMomentCueVisible,
} from '../../utils/playbookLifecycle';
import { FOOTBALL_TYPE_LABELS } from '../../utils/teamClassification';
import { FormatPicker, FootballTypePicker } from '../../components/teams/ClassificationPickers';
import FootballField from '../../components/playbook/FootballField';
import PlaybookMarker, { VISUAL_RADIUS, SELECTION_RING_RADIUS } from '../../components/playbook/PlaybookMarker';
import PlaybookTimelineControls, { PTP_PREVIEW_DURATION_MS } from '../../components/playbook/PlaybookTimelineControls';
import { usePlaybackAnimation } from '../../hooks/usePlaybackAnimation';
import {
  VIEW_WIDTH, VIEW_HEIGHT, fieldToScreen, screenToField, onLineBoundaryY, centerBoundaryY,
} from '../../components/playbook/fieldCoordinates';

// Playbook Phase B.0 (035) — Plays: a Play is a separate saved object
// built FROM a saved Formation/Front/Unit (our starting alignment) plus
// a snapshotted opponent starting look, rendered together on one field.
// See migration 035's own header comment for the full architecture
// reasoning (snapshot rule, document shape, coordinate/mirroring
// strategy, FK behavior).
//
// Phase B.1 (no new migration — see utils/playbook.js's emptyPlayDocument()
// comment) adds Post-Snap ASSIGNMENTS: a coach can select any actor
// marker (either side) and draw a custom structured movement path
// (`post_snap.assignments`, keyed by actor_id/actor_side, one per
// actor). This is intended-geometry authoring ONLY — still no timeline,
// no scrubbing, no animation, no player speed/stamina, no
// collision/engagement timing, no Vision/Threats, no Pre-Snap motion,
// no legality engine, no AI-generated plays, no athlete mapping. Path
// intersection is normal football and is never treated as an error —
// see pruneAssignments()/AssignmentPath's own comments for the
// reasoning a future PTP phase will build timing/collision semantics
// on top of, not replace.
//
// Semantics-cleanup pass (still Phase B.1, no schema/migration change):
// assignment vocabulary is now book-specific (offense vs. defense vs.
// special teams — see ASSIGNMENT_TYPE_META/assignmentTypesForUnit()
// below), Block gets a real blocking-contact endpoint instead of
// drawing through the target, and path color/style is decided by
// semantic `type` alone. Route "progression" (#1/#2/#3 read coloring)
// was deliberately NOT added this pass: the current `assignments` array
// order is NOT a safe stand-in for a coach's intended read order (a
// redraw moves an assignment to the end of the array, which would
// silently reshuffle its "read number" even though nothing about its
// priority changed) — see finishDrawing()'s replace-not-splice
// behavior. A real future implementation needs its own small persisted
// field (e.g. `progression: 1 | 2 | 3 | null` on the assignment) rather
// than inferring order from array position; that field is intentionally
// NOT added here since nothing in this pass reads it.
//
// FOOTBALL/BALL STATE — architectural note, future work, not built here:
// the ball is NOT a player assignment and never will be represented as
// one. A player's Movement assignment describes THEIR OWN motion only —
// it never implies ball possession. Ball state (snap, handoff, pitch,
// pass release, flight, catch, possession transfer, fumble/loose ball)
// belongs entirely to the future PTP/timeline/simulation system, which
// will eventually need to express something like a possession timeline
// (e.g. "0.0s Center -> 0.4s QB -> 2.2s released -> 3.0s WR") layered
// OVER player position-over-time, not mixed into `post_snap.assignments`.
// None of that exists yet and nothing here assumes it will look any
// particular way — this comment exists purely so a future implementer
// doesn't reach for "just add a football to a player's assignment" as
// the shortcut.

const OPPONENT_KEY_PREFIX_FORMATION = 'f:';
const OPPONENT_KEY_PREFIX_STARTER = 's:';

// Phase B.1 semantics-cleanup pass — Post-Snap assignments. A generic
// set, not canned route names: the fundamental object is always a
// custom structured path (`assignment.path`, a list of {x,y}
// football-coordinate waypoints); `type` is metadata describing what
// kind of assignment it is, not a separate data model. A future
// "Go/Slant/Out/In" quick-pick would just pre-fill this same `path`
// array — none exist yet (kept out of scope this pass). One assignment
// per actor: creating a new one for an actor that already has one
// REPLACES it (see finishDrawing in DualMarkerField).
//
// Which types are offered depends on which BOOK the actor's own side
// belongs to (its unit_type) — offense and defense use genuinely
// different football vocabulary, never forced onto each other:
//   offense: Route, Block, Movement
//   defense: Rush, Man, Zone, Movement
//   special_teams: Movement only (no offense/defense-specific vocabulary
//     invented for it this pass — out of this cleanup's scope)
//
// MOTION is deliberately NOT offered in the picker yet. Motion is
// PRE-SNAP player movement — a fundamentally different concept from
// everything else here, which all lives inside `post_snap.assignments`.
// Storing a "motion" entry in a container literally named `post_snap`
// would misrepresent it, and there is no Pre-Snap phase/timeline yet to
// represent it correctly. Its styling is still defined below (dashed,
// its own color) so a future Pre-Snap phase can reuse this same visual
// language without guessing — but it stays unreachable from this UI
// until that phase actually exists. This is exactly the "acceptable to
// preserve as a reserved type" option, not faked timing behavior.
//
// Rush and Route are both red-family per product direction — they're
// never offered to the same actor (Route is offense-only, Rush/Blitz are
// defense-only) but a single Play DOES show both sides at once, so each
// keeps its own distinguishable shade rather than an identical hex.
//
// Role-aware palette pass (still Phase B.1, no schema/migration change)
// adds six new persisted types on top of the semantics-cleanup set:
// run_block/pass_block (OL-specific Block variants, same rendering
// primitive as the generic 'block'), carry (offensive ball-carrier Run —
// see the note on normalizeAssignmentType() below for why this is NOT
// the old retired 'run' string), blitz/run_fit (defensive variants
// sharing Rush's path+arrow primitive), and handoff/pass (QB semantic
// path assignments with their own X/O endpoint symbols — see
// AssignmentPath's endpoint switch). Route's color is no longer a fixed
// META entry — see routeColorFor() below — since it now depends on the
// assignment's own persisted `progression` field, not its type alone.
const ASSIGNMENT_TYPE_META = {
  route:      { label: 'Route',      color: null,      dashed: false, endpoint: 'arrow' }, // color derived — see routeColorFor()
  block:      { label: 'Block',      color: '#ffffff', dashed: false, endpoint: 'bar' },
  run_block:  { label: 'Run Block',  color: '#ffffff', dashed: false, endpoint: 'bar' },
  pass_block: { label: 'Pass Block', color: '#ffffff', dashed: false, endpoint: 'bar' },
  movement:   { label: 'Movement',   color: '#9eb8d0', dashed: false, endpoint: 'arrow' },
  carry:      { label: 'Run',        color: '#e05fc4', dashed: false, endpoint: 'arrow' },
  rush:       { label: 'Pass Rush',  color: '#d1481f', dashed: false, endpoint: 'arrow' },
  blitz:      { label: 'Blitz',      color: '#b3261e', dashed: false, endpoint: 'arrow' },
  run_fit:    { label: 'Run Fit',    color: '#5a8fc4', dashed: false, endpoint: 'arrow' },
  man:        { label: 'Man',        color: '#ffffff', dashed: true,  endpoint: 'dot' },
  zone:       { label: 'Zone',       color: '#3a8ee0', dashed: false, endpoint: 'none' }, // rendered as a translucent area, not a line — see ZoneArea
  handoff:    { label: 'Handoff',    color: '#b06fe0', dashed: true,  endpoint: 'x' },
  pass:       { label: 'Pass',       color: '#4f7fe0', dashed: false, endpoint: 'o' },
  motion:     { label: 'Motion',     color: '#3ae0c9', dashed: true,  endpoint: 'arrow' }, // reserved — see note above, not offered yet
};

/** Route's on-field color is DERIVED from its persisted `progression`
 * (1|2|3|null), never stored itself — see AssignmentPath's use of this.
 * Read 1 red / Read 2 orange / Read 3 yellow / No Read white, per the
 * role-aware palette spec. */
function routeColorFor(progression) {
  if (progression === 1) return '#e33a3a';
  if (progression === 2) return '#f2994a';
  if (progression === 3) return '#e8d54a';
  return '#ffffff';
}

// Block-style rendering (white path + perpendicular contact bar) is
// shared by three coach-facing responsibilities — see blockContactPoint()
// and assignmentScreenPoints() below, both gated on this set rather than
// a single 'block' check now.
const BLOCK_FAMILY_TYPES = new Set(['block', 'run_block', 'pass_block']);

// Every type other than 'man'/'zone' is authored by drawing a
// multi-point path on the field — Man is a pure actor-to-actor reference
// committed the instant a target is chosen (no path at all), and Zone
// commits from a single field click (see commitMan()/commitZone() and
// the render branch in DualMarkerField below for where this distinction
// actually matters). These two arrays are now used only to build the
// "Other" menu's full list of currently-implemented responsibilities for
// a book — the primary role-aware palettes below decide what's shown
// directly (see ROLE_PALETTES).
const OFFENSE_ASSIGNMENT_TYPES = ['route', 'block', 'run_block', 'pass_block', 'movement', 'carry', 'handoff', 'pass'];
const DEFENSE_ASSIGNMENT_TYPES = ['rush', 'blitz', 'run_fit', 'zone', 'man', 'movement'];
const SPECIAL_TEAMS_ASSIGNMENT_TYPES = ['movement'];

function assignmentTypesForUnit(sideUnitType) {
  if (sideUnitType === 'offense') return OFFENSE_ASSIGNMENT_TYPES;
  if (sideUnitType === 'defense') return DEFENSE_ASSIGNMENT_TYPES;
  return SPECIAL_TEAMS_ASSIGNMENT_TYPES;
}

// Backward compatibility for two now-retired stored type values. Old
// saved Plays may still have these on disk — normalized at every read
// site (styling lookup, path-vs-man-vs-zone render branch) rather than
// silently breaking or requiring a migration/backfill. Nothing rewrites
// the stored value unless the coach actively edits that assignment
// again, at which point it naturally gets saved back under the current
// vocabulary.
//   'run'      -> 'movement'  (an earlier pass's Movement duplicate,
//                 RETIRED — this is intentionally NOT the same concept
//                 as the new offensive 'carry' Run responsibility added
//                 by the role-aware palette pass below. The two must
//                 stay distinct: this normalization exists to preserve
//                 §57's explicit "run -> movement" legacy-compatibility
//                 requirement for genuinely old data, while 'carry' is a
//                 brand-new, un-normalized, live type going forward. If
//                 'run' were reused for the new Run button, this same
//                 line would silently convert every new Run assignment
//                 back into Movement on the very next read.)
//   'coverage' -> 'man' if the assignment already has a target_actor_id
//                 (a targeted "coverage" was always conceptually
//                 player-to-player — that's exactly what Man is)
//              -> 'movement' if it has no target (the safest fallback:
//                 there is no recorded zone center to fall back to, and
//                 inventing one from nothing would misrepresent what
//                 the coach actually drew)
function normalizeAssignmentType(assignment) {
  const type = assignment.type;
  if (type === 'run') return 'movement';
  if (type === 'coverage') return assignment.target_actor_id ? 'man' : 'movement';
  return type;
}

// Types whose flow needs an explicit SAME-SIDE recipient chosen before
// (new) or independently of (existing, via "Change Recipient") drawing —
// Man is deliberately NOT in this set: its target is the OPPOSITE side
// and it commits immediately with no path at all (see commitMan()).
const RECIPIENT_TYPES = new Set(['handoff', 'pass']);
const ZONE_DEFAULT_RADIUS_YD = 5; // fixed for this pass — no resize control yet
// "Two football steps into the backfield" for the Pass Block automatic
// default set (§21) — a short, editable convenience path, not a real
// distance simulation. In this coordinate system "into the backfield" is
// always simply more-negative y in the ACTOR'S OWN native coordinates,
// regardless of actor_side (see this file's opponent-transform note).
const PASS_BLOCK_DEFAULT_DEPTH_YD = 1.5;

// Phase B.3f briefly added ROUTE_TAP_TOLERANCE_PX/MIN_READ_WINDOW_PROGRESS
// tuning constants here for its two-tap route-window authoring flow
// ("tap where the read begins/ends on the route"). Phase B.3g REMOVED
// that entire authoring mode (see this file's own Passing Read section
// below) in favor of one-tap Moment-based authoring — a receiver's read
// point is now captured automatically from their resolved route
// progress at the Moment, no field-tap projection/tolerance needed at
// all. projectPointOntoPolyline() itself (playbookTimeline.js) was kept
// as a general-purpose utility despite losing its only caller — see
// that file's own updated header note.

// Role-aware palette classification — derived PURELY from the actor's
// own side's unit_type + its CURRENT role_label text, never persisted
// anywhere (a coach renaming "LT" to "Tackle" simply reclassifies on the
// next render — see EDIT PLAYER below). Deliberately conservative: only
// recognizes the position abbreviations the spec calls out; anything
// else falls through to a book-generic fallback that's still fully
// usable (§15), never a dead end.
function classifyRole(sideUnitType, roleLabel) {
  const label = (roleLabel || '').trim().toUpperCase();
  if (sideUnitType === 'offense') {
    if (label === 'QB') return 'QB';
    if (label === 'HB' || label === 'RB' || label === 'FB') return 'BACK';
    if (label === 'WR') return 'WR';
    if (label === 'TE') return 'TE';
    if (['C', 'G', 'LG', 'RG', 'T', 'LT', 'RT', 'OL'].includes(label)) return 'OL';
    return 'OFFENSE_GENERIC';
  }
  if (sideUnitType === 'defense') {
    if (['DL', 'DE', 'DT', 'NT'].includes(label)) return 'DL';
    if (['LB', 'ILB', 'OLB', 'MLB'].includes(label)) return 'LB';
    if (label === 'CB') return 'CB';
    if (['S', 'FS', 'SS'].includes(label)) return 'SAFETY';
    return 'DEFENSE_GENERIC';
  }
  return 'SPECIAL_TEAMS';
}

// The PRIMARY 3-4 buttons shown directly per role classification (§6-15).
// "Other" is never listed here — it's derived separately (every type
// valid for the book, minus whatever's already primary here) so a role's
// full football vocabulary always stays reachable without forcing every
// button row to the same width.
const ROLE_PALETTES = {
  QB: [{ type: 'handoff', label: 'Handoff' }, { type: 'pass', label: 'Pass' }, { type: 'carry', label: 'Run' }],
  BACK: [{ type: 'carry', label: 'Run' }, { type: 'route', label: 'Route' }, { type: 'block', label: 'Block' }],
  WR: [{ type: 'route', label: 'Route' }, { type: 'block', label: 'Block' }, { type: 'carry', label: 'Run' }],
  TE: [{ type: 'route', label: 'Route' }, { type: 'block', label: 'Block' }, { type: 'carry', label: 'Run' }],
  OL: [{ type: 'run_block', label: 'Run Block' }, { type: 'pass_block', label: 'Pass Block' }], // 3 buttons total w/ Other, per §10
  OFFENSE_GENERIC: [{ type: 'movement', label: 'Movement' }, { type: 'route', label: 'Route' }, { type: 'block', label: 'Block' }],
  DL: [{ type: 'rush', label: 'Pass Rush' }, { type: 'run_fit', label: 'Run Fit' }, { type: 'zone', label: 'Zone' }],
  LB: [{ type: 'run_fit', label: 'Run Fit' }, { type: 'blitz', label: 'Blitz' }, { type: 'zone', label: 'Zone' }, { type: 'man', label: 'Man' }],
  CB: [{ type: 'man', label: 'Man' }, { type: 'zone', label: 'Zone' }, { type: 'blitz', label: 'Blitz' }],
  SAFETY: [{ type: 'zone', label: 'Zone' }, { type: 'man', label: 'Man' }, { type: 'run_fit', label: 'Run Fit' }],
  DEFENSE_GENERIC: [{ type: 'movement', label: 'Movement' }, { type: 'zone', label: 'Zone' }, { type: 'man', label: 'Man' }],
  SPECIAL_TEAMS: [{ type: 'movement', label: 'Movement' }],
};

/** An assignment's actor (and optional target) live on one of the two
 * independent marker arrays, disambiguated by `actor_side`/`target_side`
 * — ids alone aren't guaranteed unique across `our` vs `opponent`. */
function markersForSide(doc, side) {
  return side === 'our' ? doc.our.markers : doc.opponent.markers;
}

/** Drops any assignment whose actor no longer exists in the current
 * marker sets, and clears (never silently keeps) any target reference
 * that no longer exists — defensive cleanup so a future marker-removal
 * feature (none exists yet in the Play editor) can never leave dangling
 * assignment data behind. Pure — returns a new array, never mutates. */
function pruneAssignments(assignments, doc) {
  const ourIds = new Set(doc.our.markers.map(m => m.id));
  const opponentIds = new Set(doc.opponent.markers.map(m => m.id));
  const idsFor = side => (side === 'our' ? ourIds : opponentIds);
  return assignments
    .filter(a => idsFor(a.actor_side).has(a.actor_id))
    .map(a => (a.target_actor_id && !idsFor(a.target_side).has(a.target_actor_id)
      ? { ...a, target_side: null, target_actor_id: null }
      : a));
}

/** For a Block assignment WITH a target actor, pulls the drawn endpoint
 * back along its own approach direction so it stops at the target
 * marker's visible BOUNDARY (VISUAL_RADIUS out from its center) instead
 * of terminating at/through the target's center — the actual reported
 * bug (the target's own marker circle renders on top of the path,
 * looking like the blocker's line passes through/under the blocked
 * player). Uses the target's CURRENT position, so dragging the target
 * afterward keeps the block visually correct automatically. Falls back
 * to the raw drawn endpoint if the target no longer exists. */
function blockContactPoint(doc, assignment, rawPoints) {
  const targetMarker = markersForSide(doc, assignment.target_side).find(m => m.id === assignment.target_actor_id);
  if (!targetMarker) return rawPoints[rawPoints.length - 1];
  const targetTransform = assignment.target_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const targetPt = targetTransform(doc.field_config, targetMarker.start.x, targetMarker.start.y);
  const approachFrom = rawPoints[rawPoints.length - 2] || rawPoints[0];
  const dx = targetPt.px - approachFrom.px;
  const dy = targetPt.py - approachFrom.py;
  const dist = Math.hypot(dx, dy) || 1;
  const t = Math.max(0, (dist - VISUAL_RADIUS) / dist); // stop at the target's circle edge, not its center
  return { px: approachFrom.px + dx * t, py: approachFrom.py + dy * t };
}

/** Full screen-space point list for one assignment's path — the implicit
 * START point (never duplicated into the stored path), followed by its
 * waypoints, each mapped through that side's own render transform (plain
 * for `our`, the 180-degree opposite-side rotation for `opponent` — see
 * opponentFieldToScreen() above). Returns null if the actor no longer
 * exists (defensive; pruneAssignments should already prevent this). A
 * Block assignment with a target actor gets its final point replaced by
 * blockContactPoint() above; every other type/case uses the raw drawn
 * points unchanged.
 *
 * Phase B.3 (§12/§33) — `effectiveStart` (optional, field-relative
 * {x,y}) overrides the implicit start point; every pre-B.3 call site
 * (which never passes it) keeps getting the actor's own current
 * `marker.start` exactly as before, unchanged. This is what lets a
 * Post-Snap assignment's drawn artwork begin from an actor's RESOLVED
 * SNAP position instead of their Formation position when Pre-Snap
 * motion exists (§33's "Preferred first model": only the first segment
 * moves; every subsequent authored waypoint is untouched, since only
 * this one implicit first point ever changes, never `assignment.path`
 * itself). */
function assignmentScreenPoints(doc, assignment, effectiveStart) {
  const actor = markersForSide(doc, assignment.actor_side).find(m => m.id === assignment.actor_id);
  if (!actor) return null;
  const transform = assignment.actor_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const startXY = effectiveStart || actor.start;
  const points = [startXY, ...assignment.path].map(p => transform(doc.field_config, p.x, p.y));
  if (BLOCK_FAMILY_TYPES.has(normalizeAssignmentType(assignment)) && assignment.target_actor_id) {
    return [...points.slice(0, -1), blockContactPoint(doc, assignment, points)];
  }
  return points;
}

/** A defender's Man responsibility, connecting two actors' CURRENT
 * marker positions — never a drawn path (see this file's own note on
 * Man not being a movement path). Recomputed fresh on every render from
 * both actors' live `start` coordinates, so dragging either marker
 * moves the connection automatically with zero extra bookkeeping. Both
 * ends are pulled in by VISUAL_RADIUS so the line terminates at each
 * marker's boundary rather than running through either circle. Returns
 * null if either actor no longer exists (pruneAssignments should
 * already prevent this for the target; the actor itself is guaranteed
 * to exist by the same pruning). */
function manConnectionScreenPoints(doc, assignment) {
  const actor = markersForSide(doc, assignment.actor_side).find(m => m.id === assignment.actor_id);
  const target = assignment.target_actor_id
    ? markersForSide(doc, assignment.target_side).find(m => m.id === assignment.target_actor_id)
    : null;
  if (!actor || !target) return null;
  const actorTransform = assignment.actor_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const targetTransform = assignment.target_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const a = actorTransform(doc.field_config, actor.start.x, actor.start.y);
  const b = targetTransform(doc.field_config, target.start.x, target.start.y);
  const dx = b.px - a.px;
  const dy = b.py - a.py;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  return {
    start: { px: a.px + ux * VISUAL_RADIUS, py: a.py + uy * VISUAL_RADIUS },
    end: { px: b.px - ux * VISUAL_RADIUS, py: b.py - uy * VISUAL_RADIUS },
  };
}

/** Phase B.3f (§16/§17/§18/§20/§36/§37/§47), landmark source simplified
 * in B.3g — a Passing Read cue's two endpoints. §37's original behavior
 * change from B.3e still holds: the line does NOT terminate on the
 * target's MARKER — it terminates on a ROUTE-ATTACHED READ LANDMARK,
 * evaluated on the target's own effective route polyline via
 * pointAlongPolyline() (§47 — "the SAME effective-start-inclusive
 * polyline must be used for player motion... and the read landmark" —
 * this reuses assignmentScreenPoints() exactly as the render loop
 * already does). B.3f derived the landmark as the midpoint of a stored
 * start/end WINDOW; B.3g stores a single captured `route_progress`
 * directly (see utils/playbook.js's own header note on the cue shape),
 * so the landmark is just `pointAlongPolyline(routePoints,
 * cue.route_progress)` — simpler, same underlying geometry. The
 * landmark therefore stays tied to route GEOMETRY, not to the
 * receiver's live resolved position — it does not chase the receiver
 * around like a laser sight (§17), and a route redraw moves it
 * naturally since it's stored as normalized progress, not stale x/y
 * (§31).
 *
 * The QB endpoint, by contrast, IS still resolved from `renderDoc` (the
 * QB's CURRENT resolved position — §18/§20: "QB endpoint moves as QB
 * moves"), pulled in by VISUAL_RADIUS exactly like Man/Block so the line
 * starts at the B.3d marker boundary rather than the QB's center. The
 * landmark endpoint is left as-is (§36 — "dashed-line endpoint alone is
 * acceptable" without its own marker).
 *
 * `doc` (the real, unmodified document) supplies the target's authored
 * assignment/path — never `renderDoc`, since the landmark is meant to
 * stay fixed to authored route geometry regardless of the live scrub
 * position. `targetEffectiveStart` is the caller-resolved Snap position
 * for the target (resolveSnapPosition is component-scoped and can't be
 * called from this module-level helper). Returns null if the QB no
 * longer exists, the target's assignment is gone, or the cue has no
 * captured `route_progress` yet (a LEGACY/INCOMPLETE cue — §33/§34,
 * never draws to (0,0)). */
function passingReadCueScreenPoints(doc, renderDoc, cue, targetEffectiveStart) {
  const qb = markersForSide(renderDoc, cue.actor_side).find(m => m.id === cue.actor_id);
  const targetAssignment = doc.post_snap.assignments.find(a => a.actor_side === cue.target_side && a.actor_id === cue.target_actor_id);
  if (!qb || !targetAssignment || typeof cue.route_progress !== 'number') return null;
  const routePoints = assignmentScreenPoints(doc, targetAssignment, targetEffectiveStart);
  if (!routePoints || routePoints.length === 0) return null;
  const landmark = pointAlongPolyline(routePoints, cue.route_progress);
  if (!landmark) return null;
  const qbTransform = cue.actor_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const a = qbTransform(renderDoc.field_config, qb.start.x, qb.start.y);
  const dx = landmark.px - a.px;
  const dy = landmark.py - a.py;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  return {
    start: { px: a.px + ux * VISUAL_RADIUS, py: a.py + uy * VISUAL_RADIUS },
    end: { px: landmark.px, py: landmark.py },
  };
}

/** Converts a yards-based Zone radius into viewBox units for the
 * CURRENT field_config — the inverse of fieldCoordinates.js's own
 * visualRadiusYards(), kept local to this file since it's an
 * assignment-authoring concern, not a general field-geometry one. */
function yardsToViewboxUnits(fieldConfig, yards) {
  const beforeLos = fieldConfig?.before_los_yd ?? 15;
  const afterLos = fieldConfig?.after_los_yd ?? 15;
  const totalYards = beforeLos + afterLos || 1;
  return yards * (VIEW_HEIGHT / totalYards);
}

/** Screen-space center/radius for a Zone assignment — the zone's own
 * actor_side decides which transform applies (a zone belongs to
 * whichever side the assigned defender is on, our or opponent), exactly
 * like every other assignment type. Stored center/radius are always
 * football-relative (normalized x, yards y, yards radius) — never
 * screen pixels; this is purely a render-time conversion. */
function zoneScreenGeometry(doc, assignment) {
  const actor = markersForSide(doc, assignment.actor_side).find(m => m.id === assignment.actor_id);
  if (!actor || !assignment.zone) return null;
  const transform = assignment.actor_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const center = transform(doc.field_config, assignment.zone.center.x, assignment.zone.center.y);
  const radiusPx = yardsToViewboxUnits(doc.field_config, assignment.zone.radius);
  return { center, radiusPx };
}

/** Touch-first UX pass — an automatic "run here" responsibility cue from
 * the defender's own current marker boundary to their Zone's center.
 * Computed fresh every render from the actor's live `start` position and
 * the assignment's stored `zone.center` — NEVER stored as its own path,
 * exactly like the Man connection above. Uses the same actor_side
 * transform as zoneScreenGeometry() itself (plain for `our`, the
 * 180-degree opponent rotation for `opponent`) — no second transform. */
function zoneArrowScreenPoints(doc, assignment) {
  const geo = zoneScreenGeometry(doc, assignment);
  const actor = markersForSide(doc, assignment.actor_side).find(m => m.id === assignment.actor_id);
  if (!geo || !actor) return null;
  const transform = assignment.actor_side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const start = transform(doc.field_config, actor.start.x, actor.start.y);
  const dx = geo.center.px - start.px;
  const dy = geo.center.py - start.py;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  return {
    start: { px: start.px + ux * VISUAL_RADIUS, py: start.py + uy * VISUAL_RADIUS },
    end: { px: geo.center.px, py: geo.center.py },
  };
}

// ─────────────────────────────────────────────────────────────────────
// Duplicate-role display disambiguation — DISPLAY ONLY. Two markers
// legitimately sharing role_label "CB" stay stored as "CB"/"CB" forever
// (actor identity is always marker.id, never a label); this only builds
// a lookup of what to SHOW in a selector where the coach must tell two
// same-labeled actors apart. Ordering is by each actor's own RENDERED
// screen X position — i.e. left-to-right exactly as the PLAY VIEWER
// currently sees the field, using the same per-side transform (plain
// for `our`, the 180-degree opponent rotation for `opponent`) every
// marker/assignment already renders through — not raw document x and
// not array order. If a future Flip feature changes what's rendered
// left vs. right, these derived ordinals change with it automatically,
// which is correct: they were never persisted identity to begin with.
// ─────────────────────────────────────────────────────────────────────
function buildDisplayLabelMap(doc, side) {
  const markers = markersForSide(doc, side);
  const transform = side === 'our' ? fieldToScreen : opponentFieldToScreen;
  const screenXOf = m => transform(doc.field_config, m.start.x, m.start.y).px;

  const byLabel = new Map();
  for (const m of markers) {
    if (!byLabel.has(m.role_label)) byLabel.set(m.role_label, []);
    byLabel.get(m.role_label).push(m);
  }

  const labelForId = new Map();
  for (const group of byLabel.values()) {
    if (group.length === 1) {
      labelForId.set(group[0].id, group[0].role_label);
    } else {
      [...group]
        .sort((a, b) => screenXOf(a) - screenXOf(b))
        .forEach((m, i) => labelForId.set(m.id, `${m.role_label}${i + 1}`));
    }
  }
  return labelForId;
}

function displayLabel(labelMap, marker) {
  return labelMap.get(marker.id) || marker.role_label;
}

// Three distinct transforms exist (or will exist) in this coordinate
// system — worth naming explicitly so they're never confused:
//   A. LEFT/RIGHT MIRROR      (x, y) -> (-x, y)   — future Flip
//      Formation/Play (Trips Right -> Trips Left). NOT built yet.
//   B. VERTICAL MIRROR        (x, y) -> (x, -y)   — a plain Y
//      reflection. On its own this is NOT the correct opposite-side
//      football perspective (it would leave the offense's left side
//      drawn on the defense's left side too, which is backwards).
//   C. OPPOSITE-SIDE PERSPECTIVE / 180° ROTATION   (x, y) -> (-x, -y)
//      — what THIS file uses to render the opponent: turning to face
//      the opposite direction flips BOTH which side of the ball you're
//      on (y) AND which hand is your left (x). Future routes/blocks/
//      motion/PTP geometry will need this same 180° rotation, not a
//      bare vertical mirror.
//
/** Opponent markers are stored in their OWN native active-unit-bottom
 * convention (never pre-mirrored — see migration 035's document-shape
 * comment). Rendering them on the opposite (top) side of the same field
 * is a pure screen-side 180-degree perspective rotation (transform C
 * above — negate BOTH x and y) before mapping into viewBox space.
 * Dragging does the exact inverse: convert the pointer's screen
 * position normally, then negate both axes back into the opponent's
 * own native convention before storing/clamping/collision-checking it —
 * this is the ONLY place that transform happens; the stored document
 * never holds a rotated/mirrored coordinate. */
function opponentFieldToScreen(fieldConfig, x, y) {
  return fieldToScreen(fieldConfig, -x, -y);
}
function opponentScreenToField(fieldConfig, px, py) {
  const { x, y } = screenToField(fieldConfig, px, py);
  return { x: -x, y: -y };
}

/** Center-boundary allowance applies only to the Center of whichever
 * side is actually OFFENSE — never inferred from "our side"/"top vs
 * bottom". A defensive Play's OPPONENT is offense and gets the Center
 * allowance there; an offensive Play's OWN side gets it instead. */
function boundaryForMarker(fieldConfig, marker, sideUnitType) {
  const isCenter = sideUnitType === 'offense' && marker.role_label === 'C';
  return isCenter ? centerBoundaryY(fieldConfig) : onLineBoundaryY(fieldConfig);
}

/** The two-sided Play field: one FootballField instance, our markers
 * rendered normally (bottom, active-unit-bottom convention, unchanged
 * from Formation Builder), opponent markers rendered mirrored (top) via
 * opponentFieldToScreen(). Dragging either side clamps to that side's
 * own LOS boundary (never letting a marker's visible edge cross into
 * the other side) and runs the same Formation-editor collision guard
 * used everywhere else in Playbook — per-side only; the two sides are
 * never checked against each other, since the boundary clamp alone
 * already keeps them apart. Editor interaction only, exactly like
 * Formation Builder's own guard — nothing here is a persisted/global
 * collision rule, and nothing prevents a future Post-Snap/PTP phase
 * from allowing paths/players to cross or overlap on purpose. */

/** Phase B.3a — the `mapProgress` callback usePlaybackAnimation.js's
 * generalized hook accepts (see that file's own updated header note),
 * wiring its generic `{from, elapsedMs} -> {position, done}` contract to
 * this Play editor's two fixed presentation durations via
 * playbookLifecycle.js's own pure advanceFullScrub(). Declared at MODULE
 * scope (not inside DualMarkerField) specifically so its identity never
 * changes across renders — a stable reference here means
 * usePlaybackAnimation's own `tick` useCallback never has to rebuild
 * just because DualMarkerField re-rendered. */
function mapPlaybackProgress({ from, elapsedMs }) {
  return advanceFullScrub({
    from, elapsedMs,
    preSnapDurationMs: PRE_SNAP_PREVIEW_DURATION_MS,
    postSnapDurationMs: POST_SNAP_PREVIEW_DURATION_MS,
  });
}

function DualMarkerField({
  document: doc, onChangeOur, onChangeOpponent, onChangeAssignments, onChangePreSnap, onChangePreSnapSetPositions,
  onChangeTimeline, readOnly, ourUnitType,
}) {
  const wrapperRef = React.useRef(null);
  const draggingRef = React.useRef(null); // { side: 'our'|'opponent', id }
  const [selected, setSelected] = useState(null); // { side, id }
  // Phase B.1 field interaction mode — 'select' is the unchanged Phase
  // B.0 behavior (drag markers, click to select); 'draw' repurposes
  // field clicks into path waypoints for the selected actor instead,
  // and disables marker dragging entirely so the two gestures can never
  // conflict (see spec's own "explicit mode" requirement).
  const [fieldMode, setFieldMode] = useState('select'); // 'select' | 'draw' | 'zone'
  const [drawingPath, setDrawingPath] = useState([]); // in-progress waypoints, selected actor's own native frame
  const [assignmentType, setAssignmentType] = useState('');
  const [targetKey, setTargetKey] = useState(''); // '' | actorId of a marker (opposite side for Block/Man, SAME side for Handoff/Pass recipients)
  // Role-aware palette pass — a small panel-only state machine layered on
  // top of the existing fieldMode/drawingPath/assignmentType/targetKey
  // primitives above (none of those were removed; Route/Movement/Rush/
  // Block/Man/Zone still ultimately go through startDrawing()/commitMan()/
  // commitZone()/finishDrawing() exactly as before).
  //   pendingType    — a type the coach just picked from the palette that
  //                    needs one more explicit choice before it's armed:
  //                    'route' (progression), or a RECIPIENT_TYPES/'man'
  //                    value (target). Cleared once that choice is made.
  //   progressionChoice — staged 1|2|3|null for a Route being authored
  //                    (new or Redraw), written onto the assignment only
  //                    in finishDrawing().
  //   changingResponsibility — true only while the coach has explicitly
  //                    tapped "Change Responsibility" on an actor that
  //                    already has a committed assignment — this is the
  //                    one deliberate low-friction confirm step (§44)
  //                    that re-exposes the primary palette instead of the
  //                    existing-assignment summary view.
  const [pendingType, setPendingType] = useState(null);
  const [progressionChoice, setProgressionChoice] = useState(null);
  const [changingResponsibility, setChangingResponsibility] = useState(false);
  const [otherAnchor, setOtherAnchor] = useState(null);
  const [editPlayerOpen, setEditPlayerOpen] = useState(false);
  const [editPlayerDraft, setEditPlayerDraft] = useState('');

  // Phase B.2.3 — UNIFIED editor shell: the coach-facing [Design][PTP]
  // mode toggle is gone. There is no more `mode` state — the field
  // always shows resolved-at-scrub positions (see renderDoc below,
  // unconditional now), the timeline is always visible, and selecting a
  // player always shows contextual controls.
  //
  // Phase B.3 — FULL LIFECYCLE. What used to be "scrubPosition===0 means
  // Snap" is now split into two genuinely distinct concepts: `fullScrub`
  // (0..1) is the ONE coach-facing scrub value spanning the ENTIRE
  // FORMATION → PRE-SNAP → SNAP → POST-SNAP → END lifecycle;
  // fullScrubToLifecycle() (playbookLifecycle.js) derives which PHASE
  // that corresponds to plus a phase-LOCAL 0..1 progress within it. The
  // two reconciliation principles from B.2.3 still hold, just re-anchored
  // per phase:
  //   1. Starting-alignment DRAGGING only arms exactly AT FORMATION
  //      (fullScrub === FORMATION_FULL_SCRUB) — see handlePointerDown
  //      below. `marker.start` is now unambiguously the Play's FORMATION
  //      position only (§9 of the B.3 spec) — it is never overwritten by
  //      Pre-Snap authoring, and is no longer synonymous with "Snap" the
  //      way it was pre-B.3.
  //   2. Beginning to draw/redraw an assignment always snaps fullScrub
  //      back to THAT PHASE's own natural starting anchor first —
  //      Formation for Pre-Snap Motion, Snap for Post-Snap responsibility
  //      authoring (see beginFieldAuthoring() below) — same underlying
  //      principle as B.2.3, just phase-aware now.
  //
  // `fullScrub` is pure UI state, never persisted (matches the old
  // `scrubPosition`'s own contract exactly) — never snapped to a
  // keyframe on release. Defaults to SNAP_FULL_SCRUB (§25 of the B.3
  // spec: "open at Snap so existing Post-Snap authoring behaves exactly
  // as coaches already expect" — Formation remains one tap away).
  const [fullScrub, setFullScrub] = useState(SNAP_FULL_SCRUB);
  // Phase B.3c (§10/§11) — the arbitrary "Set Custom Position" field-
  // click placement mode (formerly `placingOverride` state + its own
  // fieldMode-independent commit branch in handleFieldPointerDown) has
  // been removed as a coach-facing authoring workflow — see the Top HUD
  // Moment section below. Legacy `actor_state[...].override` data from
  // Plays saved before this pass still RESOLVES exactly as before
  // (resolvePhaseLocalPosition's own `state?.override` check, untouched)
  // and can still be cleared via the same "Reset" action clearing a
  // normal path-progress override — only the ability to CREATE a new one
  // is gone.
  const [renamingKeyframeId, setRenamingKeyframeId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  // Phase B.3g — Passing Read authoring's own small transient UI state.
  // B.3f's three-step version (progression chooser → target chooser →
  // two-tap route-window field authoring) is gone — the whole flow is
  // now one tap (see the Top HUD Passing Read section below): eligible
  // "Read WR1"/"Read TE"/"Read HB" buttons are always visible at a
  // selected QB + Moment, each already carrying its own inferred
  // progression, so tapping one commits the cue immediately with no
  // intermediate step at all. The ONE remaining transient state is
  // `changingReadProgression` — non-null while the coach is using
  // "Change" on an EXISTING read (§29 of the B.3g spec: "show the
  // eligible receiver/read choices again," scoped to that read's own
  // progression number, e.g. changing who fills Read 1).
  const [changingReadProgression, setChangingReadProgression] = useState(null);
  // Phase B.3h (§17/§18) — the Moment PATH-progress row now rests
  // collapsed (`M2 · PATH 51%` text only, no slider) and only reveals
  // its slider while this is true — tapping "Adjust" sets it, tapping
  // the resulting "Done" (or Reset) clears it. Deliberately reset back
  // to false by selectActor() below (same pattern as every other
  // transient per-actor UI flag in this file) so switching to a
  // different actor/Moment never leaves a stale expanded slider behind.
  const [adjustingMomentPath, setAdjustingMomentPath] = useState(false);

  // Phase B.3a (§5/§6/§8) — phase-aware playback pacing. `mapPlaybackProgress`
  // is a stable MODULE-level function (defined below DualMarkerField,
  // referencing only playbookLifecycle.js's own pure advanceFullScrub()
  // + the two fixed preview-duration constants — nothing component-
  // scoped), so it never changes identity across renders and never
  // forces usePlaybackAnimation's own `tick` callback to be rebuilt.
  // `durationMs` is still passed as a harmless, unused-while-mapProgress-
  // is-present fallback (keeps the hook's original constant-rate path
  // meaningful/exercised if mapProgress were ever removed).
  const { playing, play, pause } = usePlaybackAnimation({
    position: fullScrub, onChange: setFullScrub, min: FORMATION_FULL_SCRUB, max: END_FULL_SCRUB,
    durationMs: PTP_PREVIEW_DURATION_MS, mapProgress: mapPlaybackProgress,
  });

  const opponentUnitType = doc.opponent.unit_type;
  const assignments = doc.post_snap.assignments;
  // Phase B.3g — Passing Read cues moved back to living on the Moment
  // that owns them (timeline.keyframes[].cues — see getMomentCues() and
  // this file's own qbMomentPassingReads/visiblePassingReadCue below).
  // `doc.post_snap.passing_reads` (B.3f's flat, Moment-independent
  // array) is now itself legacy/unread — see utils/playbook.js's own
  // header note.
  const preSnapAssignments = doc.pre_snap.assignments;
  // Phase B.3b (§3-§11) — static Pre-Snap SET positions: a sparse array
  // of `{actor_side, actor_id, x, y}`, one entry per NON-MOTION actor
  // who has been repositioned during Pre-Snap. See utils/playbook.js's
  // own header note on why this is its own array rather than a fake
  // one-point Motion assignment or a timeline.actor_state entry.
  const preSnapSetPositions = doc.pre_snap.set_positions;
  const findPreSnapSetPosition = (side, id) => {
    const entry = preSnapSetPositions.find(s => s.actor_side === side && s.actor_id === id);
    return entry ? { x: entry.x, y: entry.y } : null;
  };
  const hasPreSnapMotion = (side, id) => {
    const a = preSnapAssignments.find(x => x.actor_side === side && x.actor_id === id);
    return Boolean(a && a.path && a.path.length > 0);
  };
  const timeline = doc.timeline;

  // Phase-local derivation (§5/§21 of the B.3 spec) — fullScrub itself
  // is never read for keyframe lookups below; every keyframe/Moment
  // concern operates on ITS OWN phase-local progress domain instead.
  const { phase, progress: phaseProgress } = fullScrubToLifecycle(fullScrub);
  const postSnapProgress = phase === 'post_snap' ? phaseProgress : 0;
  const preSnapProgress = phase === 'pre_snap' ? phaseProgress : 1;

  // ONE shared `timeline.keyframes` array holds Moments from BOTH
  // phases (see playbookTimeline.js's own updated header note) — every
  // consumer below filters by phase FIRST (keyframePhase()), then reuses
  // playbookTimeline.js's existing phase-oblivious sort/lookup helpers
  // completely unchanged. Snap/End's real reserved keyframes are always
  // `keyframePhase()===post_snap` (never tagged, matching their existing
  // untouched meaning), so `postSnapKeyframesSorted` below is exactly
  // the same array `keyframesList` used to be pre-B.3 for any Play with
  // no Pre-Snap Moments — zero behavior change for existing Plays.
  const allKeyframes = timeline.keyframes || [];
  const postSnapKeyframesSorted = sortedKeyframes({ keyframes: allKeyframes.filter(k => keyframePhase(k) === 'post_snap') });
  const preSnapKeyframesSorted = sortedKeyframes({ keyframes: allKeyframes.filter(k => keyframePhase(k) === 'pre_snap') });

  // null whenever the coach's phase-local progress sits between marked
  // points — see findKeyframeAtPosition()'s own tolerance note. Snap/End
  // always match at their own exact 0/1 post_snap-local endpoints, same
  // as before B.3; a Pre-Snap Moment matches the same way within its own
  // phase's 0..1 domain. Formation/Snap themselves are never "selected"
  // as a Moment (no real keyframe sits at exactly the Pre-Snap phase's 0
  // or 1 boundary — see markThisPoint()'s own guard below) — this is
  // exactly what keeps Rename/Delete correctly absent while sitting on
  // either protected anchor.
  const selectedKeyframe = phase === 'post_snap'
    ? findKeyframeAtPosition(postSnapKeyframesSorted, postSnapProgress)
    : findKeyframeAtPosition(preSnapKeyframesSorted, preSnapProgress);

  const clientToScreen = (clientX, clientY) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    return {
      px: ((clientX - rect.left) / rect.width) * VIEW_WIDTH,
      py: ((clientY - rect.top) / rect.height) * VIEW_HEIGHT,
    };
  };

  const updateOurMarker = (id, patch) => {
    onChangeOur(doc.our.markers.map(m => (m.id === id ? { ...m, ...patch } : m)));
  };
  const updateOpponentMarker = (id, patch) => {
    onChangeOpponent(doc.opponent.markers.map(m => (m.id === id ? { ...m, ...patch } : m)));
  };
  // Phase B.3b (§8) — replace-by-actor, same pattern as
  // replaceActorAssignment/replacePreSnapAssignment above but for the
  // sparse set_positions array. Never creates a Moment, never touches
  // marker.start (Formation), never touches post_snap — purely "at Snap,
  // this actor is set here."
  const setPreSnapSetPosition = (side, id, xy) => {
    onChangePreSnapSetPositions(
      preSnapSetPositions.filter(s => !(s.actor_side === side && s.actor_id === id)).concat([{ actor_side: side, actor_id: id, x: xy.x, y: xy.y }]),
    );
  };
  const clearPreSnapSetPosition = (side, id) => {
    onChangePreSnapSetPositions(preSnapSetPositions.filter(s => !(s.actor_side === side && s.actor_id === id)));
  };

  // Unified selection (§B.2.3 — replaces the old separate selectMarker/
  // selectPtpActor pair now that there's no mode split to keep them
  // apart for). Works identically regardless of current fullScrub/phase —
  // "responsibility" state (assignmentType/targetKey/progression, read by
  // the RESPONSIBILITY section) resets to idle every time, since only ONE
  // selected-actor panel exists now. If playback is running, tapping a
  // player pauses it first (§10 — "pausing film to inspect something").
  const selectActor = (side, id) => {
    if (playing) pause();
    setSelected({ side, id });
    const existing = assignments.find(a => a.actor_side === side && a.actor_id === id);
    setAssignmentType(existing ? normalizeAssignmentType(existing) : '');
    setTargetKey(existing?.target_actor_id || '');
    setProgressionChoice(existing?.progression ?? null);
    setDrawingPath([]);
    setFieldMode('select');
    // Selecting an actor never itself arms authoring (§18 of the
    // touch-first pass, still true here) and never assumes the coach
    // wants to change an already-committed responsibility (§43/§44) —
    // both palette-flow states reset to their idle defaults every time.
    setPendingType(null);
    setChangingResponsibility(false);
    setOtherAnchor(null);
    // Phase B.3h (§17/§18) — a newly-selected actor always starts with
    // the Moment PATH row collapsed, never carrying over a previous
    // actor's expanded-slider state.
    setAdjustingMomentPath(false);
    setChangingReadProgression(null);
  };

  const handlePointerDown = (side, id) => (e) => {
    if (readOnly) return;
    // Phase B.4a — THREAT target selection. A tap on ANY marker while
    // 'threat_select' is active is the coach's explicit, deliberate
    // defender choice (§Target Selection: "the coach ALWAYS chooses
    // explicitly — never infer via nearest-defender or any geometry").
    // Tapping a marker on the SAME side as the offensive owner
    // (`selected.side`) is not a valid defender target — per spec,
    // "offensive markers must NOT be accepted as targets" — so it's
    // silently ignored (mode stays active, no cue mutation) rather than
    // treated as a new selection; the coach simply taps a defender to
    // proceed. This branch must run BEFORE the `fieldMode !== 'select'`
    // early-return below so a threat-select tap is never mistaken for a
    // mid-draw/zone waypoint tap and never falls through to
    // selectActor().
    if (fieldMode === 'threat_select') {
      e.stopPropagation();
      if (side !== selected.side) {
        commitThreat(side, id);
      }
      return;
    }
    // Mid-draw/zone-placement taps must bubble to handleFieldPointerDown
    // unchanged (adding a waypoint / committing the zone center) — a
    // coach tapping near/on another marker while actively drawing must
    // never be intercepted as a selection change.
    if (fieldMode !== 'select') return;
    e.stopPropagation();
    // Starting-alignment DRAGGING only arms exactly at FORMATION (§9/§10
    // of the B.3 spec — core reconciliation principle, see this
    // component's own header note above). Away from Formation, the tap
    // still selects normally for inspection; it just never starts a
    // drag, since the rendered position everywhere else (including
    // Snap) is a resolved/interpolated lifecycle position, not the
    // actual stored Formation start.
    //
    // Phase B.3b (§4/§8/§9) — a SECOND drag mode: anywhere else within
    // Pre-Snap (progress > 0, i.e. NOT exactly at Formation), a
    // NON-MOTION actor may also be dragged — this edits that actor's
    // static `pre_snap.set_positions` entry instead of Formation, and is
    // completely disabled for an actor who already has explicit Motion
    // (§9 — "the least surprising UX": a Motion actor's Snap position is
    // unambiguously their Motion endpoint; requiring Motion
    // editing/redrawing instead of a second, competing drag target
    // avoids two sources of truth fighting each other). Away from
    // Formation with a Motion actor selected, the tap still just
    // selects (falls through both branches below) — unchanged.
    if (fullScrub === FORMATION_FULL_SCRUB) {
      e.target.setPointerCapture(e.pointerId);
      draggingRef.current = { side, id, mode: 'formation' };
    } else if (phase === 'pre_snap' && !hasPreSnapMotion(side, id)) {
      e.target.setPointerCapture(e.pointerId);
      draggingRef.current = { side, id, mode: 'pre_snap_set' };
    }
    selectActor(side, id);
  };

  const handlePointerMove = (e) => {
    if (readOnly || !draggingRef.current) return;
    const { side, id, mode } = draggingRef.current;
    const { px, py } = clientToScreen(e.clientX, e.clientY);

    if (mode === 'pre_snap_set') {
      // Phase B.3b (§8) — static Pre-Snap set-position drag. Deliberately
      // NOT run through boundaryForMarker()'s Formation-only onside/
      // Center LOS clamp (a Pre-Snap set position isn't a Formation-
      // legality concept — §8/§14: "we are not officiating the Play")
      // and NOT run through resolveMarkerCollisions() either (Motion
      // endpoints have never been collision-guarded against each other
      // or against static actors; keeping the same lack of a guard here
      // keeps the two Pre-Snap mechanisms consistent with each other,
      // per §14's "no legality engine"). Only a basic sideline clamp on
      // x (same -1..1 every other draggable point in this file uses) —
      // never touches marker.start (Formation), never touches
      // post_snap, never creates a Moment, never modifies the opponent
      // transform (mirrors it exactly like every other opponent-side
      // coordinate here).
      const { x, y } = side === 'our' ? screenToField(doc.field_config, px, py) : opponentScreenToField(doc.field_config, px, py);
      const clampedX = Math.max(-1, Math.min(1, x));
      setPreSnapSetPosition(side, id, { x: clampedX, y });
      return;
    }

    // mode === 'formation' — unchanged Formation-authoring drag.
    if (side === 'our') {
      const { x, y: rawY } = screenToField(doc.field_config, px, py);
      const marker = doc.our.markers.find(m => m.id === id);
      const boundary = boundaryForMarker(doc.field_config, marker, ourUnitType);
      const clampedX = Math.max(-1, Math.min(1, x));
      const clampedY = Math.min(boundary, rawY);
      const resolved = resolveMarkerCollisions(doc.field_config, id, { x: clampedX, y: clampedY }, doc.our.markers);
      updateOurMarker(id, { start: resolved });
    } else {
      const { x, y: rawY } = opponentScreenToField(doc.field_config, px, py);
      const marker = doc.opponent.markers.find(m => m.id === id);
      const boundary = boundaryForMarker(doc.field_config, marker, opponentUnitType);
      const clampedX = Math.max(-1, Math.min(1, x));
      const clampedY = Math.min(boundary, rawY);
      const resolved = resolveMarkerCollisions(doc.field_config, id, { x: clampedX, y: clampedY }, doc.opponent.markers);
      updateOpponentMarker(id, { start: resolved });
    }
  };

  const handlePointerUp = () => {
    draggingRef.current = null;
  };

  const replaceActorAssignment = (next) => assignments.filter(a => !(a.actor_side === selected.side && a.actor_id === selected.id)).concat(next ? [next] : []);
  // Phase B.3 (§36/§37) — Pre-Snap's own replace-by-actor semantics,
  // identical mechanism to replaceActorAssignment() above, deliberately
  // kept as its OWN function (rather than parameterizing one function
  // over `assignments`/`preSnapAssignments`) since the two pools are
  // conceptually independent and are never meant to be confused at a
  // call site — see utils/playbook.js's own note on why pre_snap never
  // reuses post_snap.assignments as its storage.
  const replacePreSnapAssignment = (next) => preSnapAssignments.filter(a => !(a.actor_side === selected.side && a.actor_id === selected.id)).concat(next ? [next] : []);

  // Phase B.1: field background clicks. In 'select' mode this is the
  // unchanged Phase B.0 deselect-on-background behavior. In 'draw' mode
  // it appends a waypoint (Route/Block/Rush/Movement) — deliberately NOT
  // run through the Formation-phase LOS boundary clamp or the marker
  // collision guard: Post-Snap paths must be free to cross the LOS and
  // cross each other (this is normal football; see this file's own
  // header note on path intersection vs. future PTP timing). In 'zone'
  // mode a single tap both defines the zone center AND commits it —
  // Zone is area-based, not a multi-point path, so there is no separate
  // Finish step (see commitZone()).
  //
  const handleFieldPointerDown = (e) => {
    if (fieldMode === 'zone') {
      if (!selected) { setFieldMode('select'); return; }
      const { px, py } = clientToScreen(e.clientX, e.clientY);
      const { x, y } = selected.side === 'our'
        ? screenToField(doc.field_config, px, py)
        : opponentScreenToField(doc.field_config, px, py);
      commitZone(x, y);
      return;
    }
    // Phase B.4a — a background/empty-field tap (or a tap that fell
    // through handlePointerDown's own same-side ignore above) while
    // 'threat_select' is active just exits selection mode WITHOUT
    // deselecting the offensive owner or mutating any cue ("clicking
    // empty field creates nothing"). Deliberately its own branch rather
    // than falling into the generic `fieldMode !== 'draw'` fallback
    // below, which would call setSelected(null) — losing the offensive
    // owner on a stray empty-field tap would be surprising and is not
    // what the spec's Cancel/Escape pattern asks for.
    if (fieldMode === 'threat_select') {
      setFieldMode('select');
      return;
    }
    // Phase B.3f briefly added a 'read_window_start'/'read_window_end'
    // two-tap route-projection branch here for its route-window
    // authoring flow. Phase B.3g removed it entirely (§35 of that spec)
    // — Passing Read authoring no longer touches the field at all; see
    // the Top HUD Passing Read section below for the current one-tap
    // flow.
    if (fieldMode !== 'draw') {
      setSelected(null);
      setPendingType(null);
      setChangingResponsibility(false);
      return;
    }
    if (!selected) return;
    const { px, py } = clientToScreen(e.clientX, e.clientY);
    const { x, y } = selected.side === 'our'
      ? screenToField(doc.field_config, px, py)
      : opponentScreenToField(doc.field_config, px, py);
    setDrawingPath(p => [...p, { x, y }]);
  };

  // §11/§12 of the B.2.3 spec — "player responsibility authoring edits
  // the underlying Play from Snap." Phase B.3 generalizes this to BOTH
  // phases: every entry point into actually authoring path geometry
  // snaps fullScrub back to THAT PHASE's own natural starting anchor
  // FIRST — Snap for Post-Snap responsibility authoring (drawing, Zone
  // placement, Pass Block's auto-default — unchanged target), Formation
  // for Pre-Snap Motion authoring (startPreSnapMotion() below — new).
  // `phase` here is read at the moment authoring BEGINS and never
  // changes again mid-draw (the bottom HUD, including every anchor/
  // slider/nudge control, is fully `disabled` for the whole
  // fieldMode!=='select' duration — see the `disabled` prop passed to
  // PlaybookTimelineControls below), so finishDrawing() can safely still
  // trust `phase` at commit time to know which pool it's authoring for.
  // Also pauses playback defensively (Play is already disabled whenever
  // fieldMode !== 'select', so this should never actually find playback
  // running, but costs nothing to be safe).
  const beginFieldAuthoring = () => {
    if (playing) pause();
    const anchor = phase === 'pre_snap' ? FORMATION_FULL_SCRUB : SNAP_FULL_SCRUB;
    if (fullScrub !== anchor) setFullScrub(anchor);
  };
  const startDrawing = () => { beginFieldAuthoring(); setDrawingPath([]); setPendingType(null); setFieldMode('draw'); };
  const cancelDrawing = () => { setDrawingPath([]); setFieldMode('select'); };
  const startZonePlacement = () => { beginFieldAuthoring(); setPendingType(null); setFieldMode('zone'); };
  const cancelZonePlacement = () => setFieldMode('select');
  // Phase B.3 (§7/§26/§29) — Pre-Snap Motion v1's ONE entry point: no
  // sub-choice/pendingType step at all (Motion has no progression,
  // target, or recipient concept — see utils/playbook.js's own note on
  // why Pre-Snap stays a single generic type this pass), straight into
  // drawing exactly like Movement/Rush/Block already do on the Post-Snap
  // side.
  const startPreSnapMotion = () => { beginFieldAuthoring(); setDrawingPath([]); setAssignmentType('motion'); setFieldMode('draw'); };
  const clearPreSnapMotion = () => {
    onChangePreSnap(preSnapAssignments.filter(a => !(a.actor_side === selected.side && a.actor_id === selected.id)));
  };

  const finishDrawing = () => {
    setFieldMode('select');
    setChangingResponsibility(false);
    if (drawingPath.length === 0) return; // nothing drawn = cancel, existing assignment (if any) untouched
    // Phase B.3 (§29/§36/§37) — `phase` is still whatever it was when
    // beginFieldAuthoring() anchored it (see that function's own note —
    // it cannot have changed mid-draw), so it safely tells us which pool
    // this drawing session belongs to. Pre-Snap Motion commits into its
    // own `pre_snap.assignments`, never post_snap.assignments — see
    // utils/playbook.js's own note on why the two pools stay separate.
    if (phase === 'pre_snap') {
      onChangePreSnap(replacePreSnapAssignment({
        id: newMarkerId(), actor_side: selected.side, actor_id: selected.id,
        type: 'motion', path: drawingPath,
      }));
      // Phase B.3b (§5) — Motion now unambiguously owns this actor's
      // Snap position; a leftover static set_position (e.g. authored
      // before the coach decided to add Motion instead) would become
      // permanently dead/unreachable data under resolveSnapPosition()'s
      // own precedence, so clear it here rather than let it linger.
      if (findPreSnapSetPosition(selected.side, selected.id)) {
        clearPreSnapSetPosition(selected.side, selected.id);
      }
      setDrawingPath([]);
      return;
    }
    const base = {
      id: newMarkerId(), actor_side: selected.side, actor_id: selected.id,
      type: assignmentType, path: drawingPath,
      target_side: null, target_actor_id: null,
    };
    if (assignmentType === 'route') {
      base.progression = progressionChoice ?? null;
    } else if (RECIPIENT_TYPES.has(assignmentType)) {
      // Handoff/Pass — recipient is the SAME side as the actor (a
      // teammate, never an opponent), chosen in the recipient step
      // before drawing armed (see the recipient <Select> below).
      base.target_side = selected.side;
      base.target_actor_id = targetKey || null;
    } else if (BLOCK_FAMILY_TYPES.has(assignmentType)) {
      // §18/§19: normal blocking never lets the coach ATTACH a new
      // target in this UI, but a Redraw of an OLD assignment that
      // already had one (from before this pass) must not silently drop
      // it — preserve whatever was already committed, unchanged.
      base.target_side = selectedAssignment?.target_side ?? null;
      base.target_actor_id = selectedAssignment?.target_actor_id ?? null;
    }
    onChangeAssignments(replaceActorAssignment(base));
    setDrawingPath([]);
  };

  // Man is a pure actor-to-actor reference — never a drawn path (see
  // this file's own header note on Man vs. movement paths). Committed
  // the instant a Target is picked; choosing "No target" clears it,
  // since a targetless Man assignment isn't meaningful.
  const commitMan = (target) => {
    onChangeAssignments(replaceActorAssignment(target ? {
      id: newMarkerId(), actor_side: selected.side, actor_id: selected.id,
      type: 'man', path: [],
      target_side: selected.side === 'our' ? 'opponent' : 'our', target_actor_id: target,
    } : null));
    setPendingType(null);
    setChangingResponsibility(false);
  };

  // Zone is area-based, not path-based — one field tap defines the
  // center and commits immediately (see handleFieldPointerDown above).
  // Radius is a fixed default this pass (ZONE_DEFAULT_RADIUS_YD, stored
  // in yards so it stays meaningful if a future pass adds a resize
  // control) — no drawing/resize UI yet.
  const commitZone = (x, y) => {
    onChangeAssignments(replaceActorAssignment({
      id: newMarkerId(), actor_side: selected.side, actor_id: selected.id,
      type: 'zone', path: [], target_side: null, target_actor_id: null,
      zone: { center: { x, y }, radius: ZONE_DEFAULT_RADIUS_YD },
    }));
    setFieldMode('select');
    setChangingResponsibility(false);
  };

  // Pass Block's automatic default (§21) — a short straight drop
  // "roughly two football steps" into the actor's OWN backfield (more-
  // negative y in their own native coordinates), committed immediately
  // so the coach sees a real blocking T without drawing anything. It
  // remains a completely normal, fully-editable/redrawable assignment
  // from this point on — nothing distinguishes it from a hand-drawn one
  // except how it was first created.
  const commitPassBlockDefault = () => {
    if (!selected) return;
    beginFieldAuthoring();
    const actor = markersForSide(doc, selected.side).find(m => m.id === selected.id);
    if (!actor) return;
    onChangeAssignments(replaceActorAssignment({
      id: newMarkerId(), actor_side: selected.side, actor_id: selected.id,
      type: 'pass_block',
      path: [{ x: actor.start.x, y: actor.start.y - PASS_BLOCK_DEFAULT_DEPTH_YD }],
      target_side: null, target_actor_id: null,
    }));
    setAssignmentType('pass_block');
    setPendingType(null);
    setChangingResponsibility(false);
  };

  // Dispatch for a freshly-picked palette/Other type — the one place
  // that decides which of the five different "arm" behaviors a type
  // gets. Only ever called when there's no existing assignment for this
  // actor, OR the coach explicitly tapped "Change Responsibility" (see
  // chooseAssignmentType below) — so this never silently overwrites
  // saved work (§18 touch-first principle, §44 here).
  const armType = (type) => {
    setAssignmentType(type);
    setTargetKey('');
    setProgressionChoice(null);
    if (type === 'route') { setPendingType('route'); return; }
    if (type === 'man' || RECIPIENT_TYPES.has(type)) { setPendingType(type); return; }
    if (type === 'zone') { startZonePlacement(); return; }
    if (type === 'pass_block') { commitPassBlockDefault(); return; }
    // movement, carry, block, run_block, rush, blitz, run_fit — straight
    // into path drawing, no intermediate step.
    startDrawing();
  };

  const chooseAssignmentType = (type) => {
    setChangingResponsibility(false);
    armType(type);
  };

  // "Change Recipient" on an ALREADY-committed Handoff/Pass — patches
  // target_actor_id in place, deliberately keeping the existing drawn
  // path untouched (distinct from Redraw, which re-authors the
  // geometry) — matches §43's "Change Recipient / Redraw / Clear" as
  // three separate, independent actions.
  const changeRecipient = (newTargetId) => {
    if (!selected) return;
    onChangeAssignments(assignments.map(a => (
      a.actor_side === selected.side && a.actor_id === selected.id
        ? { ...a, target_actor_id: newTargetId || null }
        : a
    )));
    setTargetKey(newTargetId || '');
    setPendingType(null);
  };

  // In-place progression edit (§26/§43's "Edit progression") — changes
  // only the persisted progression field on an existing Route, never
  // touches its drawn path.
  const setRouteProgressionInPlace = (value) => {
    if (!selected) return;
    onChangeAssignments(assignments.map(a => (
      a.actor_side === selected.side && a.actor_id === selected.id
        ? { ...a, progression: value }
        : a
    )));
    setProgressionChoice(value);
  };

  const clearAssignment = () => {
    onChangeAssignments(assignments.filter(a => !(a.actor_side === selected.side && a.actor_id === selected.id)));
    setPendingType(null);
    setChangingResponsibility(false);
  };

  // "+ Mark Moment" — creates a coach keyframe at the CURRENT phase-local
  // progress exactly, never moving the scrubber or any other Moment.
  // Shared by PlaybookTimelineControls's own button and the per-actor
  // panel's "Mark this moment to adjust..." shortcut so both stay in
  // sync with the same duplicate-tolerance check. Playback is paused by
  // the caller before this ever runs (PlaybookTimelineControls' own
  // manualMarkMoment wrapper) — marking mid-play would be a confusing
  // moving target otherwise.
  //
  // Phase B.3 (§52) — phase-aware: a Post-Snap Moment behaves EXACTLY as
  // before (findKeyframeAtPosition against the real Snap(0)/End(1)
  // keyframes already rejects duplicates sitting on those). Pre-Snap has
  // no real stored Formation/Snap-boundary keyframes to lean on for that
  // same rejection (see resolveSnapPosition()'s own note on why none are
  // persisted), so an explicit isAtFormation/isAtSnap guard does the
  // same job — never create a redundant Moment coinciding with either
  // protected anchor.
  const markThisPoint = () => {
    if (phase === 'pre_snap') {
      if (isAtFormation(fullScrub) || isAtSnap(fullScrub)) return;
      const existing = findKeyframeAtPosition(preSnapKeyframesSorted, preSnapProgress);
      if (existing) return;
      onChangeTimeline(addKeyframeAtPosition(timeline, preSnapProgress, undefined, 'pre_snap'));
      return;
    }
    const existing = findKeyframeAtPosition(postSnapKeyframesSorted, postSnapProgress);
    if (existing) return; // already a point here (Snap/End/existing marked point)
    onChangeTimeline(addKeyframeAtPosition(timeline, postSnapProgress, undefined, 'post_snap'));
  };

  // Phase B.3g (§13) — every OUR offensive actor whose Post-Snap Route
  // currently carries the given progression number. Reuses the EXISTING
  // route-progression field verbatim (never duplicates it into cue
  // data). Explicitly typed to 'route' (not just "has a `.progression`
  // field") — no other assignment type ever sets `.progression` today,
  // but being explicit here keeps that a documented invariant of THIS
  // function rather than an accidental side effect of upstream data
  // shape. Used both for the "add" row's one-tap buttons (unfiltered by
  // any existing cue — see eligibleReadRoutes below) and for the
  // progression-scoped "Change" target picker (§29).
  const eligibleReadTargets = (progression) => assignments
    .filter(a => a.actor_side === 'our' && normalizeAssignmentType(a) === 'route' && a.progression === progression)
    .map(a => markersForSide(doc, 'our').find(m => m.id === a.actor_id))
    .filter(Boolean);

  const startChangeRead = (progression) => setChangingReadProgression(progression);
  const cancelChangeRead = () => setChangingReadProgression(null);

  // Phase B.3g (§16/§17/§18) — the ENTIRE Passing Read authoring flow:
  // one tap. `moment` must be the currently SELECTED Moment — every
  // caller (the Top HUD's "Read WR1"/etc. buttons, and the "Change"
  // flow's own target picker) only ever renders while
  // selectedKeyframe.kind === 'keyframe', which by construction means
  // the coach is sitting exactly at THAT Moment's own fullScrub
  // position right now. resolveActorRouteProgressAtFullScrub() therefore
  // captures the target's route progress AT this Moment — honoring any
  // Moment path-progress override the coach staged for them here (§18:
  // "Moment 1 at 40%, WR1 override at M1 = 55% → stored route_progress
  // = 0.55, not global 0.40") — automatically, with zero extra
  // bookkeeping. Replace-by-(actor,progression): a QB may have up to
  // three simultaneous cues on one Moment (one per Read number, see
  // qbMomentPassingReads below), but only ONE per progression —
  // authoring/changing Read 1 replaces any existing Read 1 on THIS
  // Moment without touching Read 2/Read 3 or any other Moment's own
  // reads (§10 test: "no further taps required").
  const commitPassingRead = (moment, progression, targetSide, targetActorId) => {
    const routeProgress = resolveActorRouteProgressAtFullScrub(targetSide, targetActorId, fullScrub);
    const nextCues = getMomentCues(moment)
      .filter(c => !(c.type === 'pass_read' && c.actor_side === selected.side && c.actor_id === selected.id && c.progression === progression))
      .concat([{
        id: newMarkerId(), type: 'pass_read',
        actor_side: selected.side, actor_id: selected.id,
        progression, target_side: targetSide, target_actor_id: targetActorId,
        route_progress: routeProgress,
      }]);
    onChangeTimeline(replaceKeyframeCues(timeline, moment.id, nextCues));
    setChangingReadProgression(null);
  };

  const removeMomentPassingRead = (moment, progression) => {
    const nextCues = getMomentCues(moment).filter(c => !(c.type === 'pass_read' && c.actor_side === selected.side && c.actor_id === selected.id && c.progression === progression));
    onChangeTimeline(replaceKeyframeCues(timeline, moment.id, nextCues));
  };

  // Phase B.4a — THREAT authoring. Enters a dedicated field-selection
  // mode (fieldMode 'threat_select', handled alongside 'draw'/'zone' in
  // handleFieldPointerDown/handlePointerDown below) rather than a modal
  // — "prefer direct field selection" — mirroring the SAME
  // beginFieldAuthoring()-free simplicity Passing Read's own one-tap
  // flow already established (no path to draw, no Snap re-anchor
  // needed: the coach is already sitting exactly on `selectedMoment`
  // when this is offered, and stays there throughout selection since
  // `fullScrub` is untouched by any of this). `selected` (the offensive
  // owner) stays pinned throughout — handlePointerDown's own existing
  // `fieldMode !== 'select'` early-return already means clicking ANY
  // marker while in 'threat_select' never re-triggers selectActor().
  const startThreatSelect = () => setFieldMode('threat_select');
  const cancelThreatSelect = () => setFieldMode('select');

  // One Threat cue per offensive owner per Moment (§B.4a rule) —
  // replace-by-(actor) identity, same pattern as commitPassingRead()'s
  // own replace-by-(actor,progression) just above, minus the extra
  // progression dimension (Threat has no numbered slots — an owner has
  // at most ONE Threat at a given Moment, full stop). Never infers a
  // target — `defenderSide`/`defenderId` always come from an explicit
  // coach click (handlePointerDown below), never nearest-defender/
  // geometry/AI logic.
  const commitThreat = (defenderSide, defenderId) => {
    if (!selected || !selectedMoment) { setFieldMode('select'); return; }
    const nextCues = getMomentCues(selectedMoment)
      .filter(c => !(c.type === 'threat' && c.actor_side === selected.side && c.actor_id === selected.id))
      .concat([{
        id: newMarkerId(), type: 'threat',
        actor_side: selected.side, actor_id: selected.id,
        target_side: defenderSide, target_actor_id: defenderId,
      }]);
    onChangeTimeline(replaceKeyframeCues(timeline, selectedMoment.id, nextCues));
    setFieldMode('select');
  };

  const removeMomentThreat = () => {
    if (!selected || !selectedMoment) return;
    const nextCues = getMomentCues(selectedMoment).filter(c => !(c.type === 'threat' && c.actor_side === selected.side && c.actor_id === selected.id));
    onChangeTimeline(replaceKeyframeCues(timeline, selectedMoment.id, nextCues));
  };

  const selectedMarker = selected ? markersForSide(doc, selected.side).find(m => m.id === selected.id) : null;
  const selectedAssignment = selected
    ? assignments.find(a => a.actor_side === selected.side && a.actor_id === selected.id)
    : null;
  // Phase B.3 (§26) — the Pre-Snap analogue of selectedAssignment above,
  // used only by the Top HUD's Pre-Snap branch to decide "+ Add Motion"
  // vs. an existing-Motion summary + Redraw/Clear.
  const selectedPreSnapAssignment = selected
    ? preSnapAssignments.find(a => a.actor_side === selected.side && a.actor_id === selected.id)
    : null;
  // Phase B.3b (§10/§39) — the selected actor's static Pre-Snap set
  // position (if any), and whether the coach is currently sitting
  // exactly at the Formation anchor — both drive the Pre-Snap Top HUD's
  // contextual messaging/reset-button visibility below.
  const selectedSetPosition = selected ? findPreSnapSetPosition(selected.side, selected.id) : null;
  const atFormationAnchor = fullScrub === FORMATION_FULL_SCRUB;

  // Phase B.3g (§8/§12/§15/§38) — Passing Read authoring is OUR-QB-only,
  // Post-Snap-ONLY, and — reverting B.3f's own change back — ONCE AGAIN
  // requires sitting on a coach-created Post-Snap Moment: "Moment: WHEN
  // does the QB evaluate this read? / Passing Read: QB action/decision
  // attached to that Moment" (§38). `selectedMoment` is null at Snap/End
  // (real but non-coach-created keyframes) or anywhere unmarked — the
  // Top HUD shows a compact "+ Moment" prompt in that case instead of
  // any Read choices (§15).
  const isSelectedOurQB = Boolean(selected && selected.side === 'our' && classifyRole(ourUnitType, selectedMarker?.role_label) === 'QB');
  const atPostSnap = phase === 'post_snap';
  const selectedMoment = (atPostSnap && selectedKeyframe && selectedKeyframe.kind === 'keyframe') ? selectedKeyframe : null;
  // Phase B.4a (§Offensive Owner) — Threat authoring begins from ANY
  // selected offensive player (QB/RB/WR/etc — not QB-only like Passing
  // Read), gated on this Play's own `our` side actually BEING the
  // offense (`ourUnitType === 'offense'`) — "this first Threat feature
  // is offense-oriented teaching." A defensive Play (our === defense)
  // deliberately does not expose Threat authoring from a selected `our`
  // actor in this pass (§Offensive Owner: "we can generalize later if
  // needed") — reusing the EXISTING `ourUnitType` in scope rather than
  // inventing a new 'offense'/'defense' stored convention (the cue
  // itself still stores plain 'our'/'opponent', exactly like every other
  // cue/assignment in this file).
  const isSelectedOurOffense = Boolean(selected && selected.side === 'our' && ourUnitType === 'offense');
  // Every Passing Read cue currently authored ON THE SELECTED MOMENT for
  // the selected QB — a QB may have up to three simultaneously on one
  // Moment (one per progression number; see commitPassingRead()'s own
  // replace-by-(actor,progression) identity above).
  const qbMomentPassingReads = (isSelectedOurQB && selectedMoment)
    ? getMomentCues(selectedMoment).filter(c => c.type === 'pass_read' && c.actor_side === selected.side && c.actor_id === selected.id)
    : [];
  // Every OUR offensive Route assignment with a numeric progression —
  // the full pool of one-tap "Read WR1"/"Read TE"/"Read HB" candidates
  // (§12/§13: infer progression from the receiver's own route, never a
  // separate "choose progression first" step). The Top HUD's "add" row
  // below further filters this down to progressions NOT already covered
  // by qbMomentPassingReads, so re-tapping an already-authored Read
  // isn't offered as a redundant duplicate button.
  const eligibleReadRoutes = assignments.filter(a => a.actor_side === 'our' && normalizeAssignmentType(a) === 'route' && typeof a.progression === 'number');
  // Block/Man targets are the OPPOSITE side (an opponent to block or
  // cover) — unchanged from before this pass.
  const targetSide = selected ? (selected.side === 'our' ? 'opponent' : 'our') : null;
  const targetSideMarkers = selected ? markersForSide(doc, targetSide) : [];
  // Handoff/Pass recipients are a SAME-side teammate — the QB never
  // hands off to (or "intends" a pass toward) the opposing team. The
  // acting player itself is excluded from its own recipient list.
  const recipientSideMarkers = selected
    ? markersForSide(doc, selected.side).filter(m => m.id !== selected.id)
    : [];
  // Offense/defense/special-teams each get their own vocabulary — never
  // the other book's terms (e.g. a defender is never offered "Route").
  const selectedSideUnitType = selected ? (selected.side === 'our' ? ourUnitType : opponentUnitType) : null;

  // Role-aware palette pass — which 3-4 primary buttons this actor gets,
  // derived from its CURRENT role_label (never persisted — see
  // classifyRole()'s own header note), plus every other currently-
  // implemented type for this book that isn't already primary (the
  // "Other" menu's contents).
  const roleClass = selected ? classifyRole(selectedSideUnitType, selectedMarker?.role_label) : null;
  const primaryActions = roleClass ? (ROLE_PALETTES[roleClass] || []) : [];
  const primaryTypeSet = new Set(primaryActions.map(p => p.type));
  const otherTypes = selectedSideUnitType
    ? assignmentTypesForUnit(selectedSideUnitType).filter(t => !primaryTypeSet.has(t))
    : [];

  // Duplicate-role display disambiguation (see buildDisplayLabelMap()'s
  // own header note) — one map per side, used only in selectors where
  // the coach must tell same-labeled actors apart; marker circles on
  // the field keep showing the plain role_label. Phase B.3g (§41/§42) —
  // memoized on `doc` alone: buildDisplayLabelMap() reads ONLY `doc`
  // (Formation-relative `m.start`, never renderDoc's resolved position —
  // see its own header note), so its result is invariant across a
  // playback tick (`fullScrub` changing does not change `doc`). Before
  // this pass it was recomputed from scratch on every single animation
  // frame despite never actually changing during playback — see this
  // component's own new header note below on the live-scrubber fix for
  // the full explanation of why that mattered.
  const ourDisplayLabels = React.useMemo(() => buildDisplayLabelMap(doc, 'our'), [doc]);
  const opponentDisplayLabels = React.useMemo(() => buildDisplayLabelMap(doc, 'opponent'), [doc]);
  const targetDisplayLabels = targetSide === 'our' ? ourDisplayLabels : opponentDisplayLabels;
  const selectedDisplayLabels = selected?.side === 'our' ? ourDisplayLabels : opponentDisplayLabels;

  // Contextual authoring instruction text, named by the actor's (and,
  // for legacy targeted Block/Man/Handoff/Pass, the target/recipient's)
  // DISAMBIGUATED DISPLAY label only (see buildDisplayLabelMap() above)
  // — never role_label alone, so duplicates always read unambiguously.
  const selectedLabel = selectedMarker ? displayLabel(selectedDisplayLabels, selectedMarker) : '';
  // Phase B.3c (§4) — "OUR"/"OPP" removed from every Top HUD header line
  // (see the header/resting-summary/Pre-Snap Typography lines below) —
  // the selected marker's own color + selection ring already
  // communicate side; a former `sideLabel` prefix was judged redundant
  // for the compact-toolbar goal. `selected.side` itself is untouched —
  // this was always display-only.
  const targetMarkerForLabel = targetKey ? targetSideMarkers.find(m => m.id === targetKey) : null;
  const targetLabel = targetMarkerForLabel ? displayLabel(targetDisplayLabels, targetMarkerForLabel) : '';
  // Recipient (Handoff/Pass) is looked up on the SAME side as the actor
  // — selectedDisplayLabels already IS that side's map.
  const recipientMarkerForLabel = selected && targetKey
    ? markersForSide(doc, selected.side).find(m => m.id === targetKey)
    : null;
  const recipientLabel = recipientMarkerForLabel ? displayLabel(selectedDisplayLabels, recipientMarkerForLabel) : '';
  // A pre-existing (legacy or previously-attached) Block-family target —
  // shown read-only in the existing-assignment summary (§49's "optional
  // legacy Block target display"); this UI never lets a coach attach a
  // new one (§18/§19).
  const legacyBlockTargetLabel = (() => {
    if (!selectedAssignment || !BLOCK_FAMILY_TYPES.has(normalizeAssignmentType(selectedAssignment))) return '';
    if (!selectedAssignment.target_actor_id) return '';
    const marker = markersForSide(doc, selectedAssignment.target_side).find(m => m.id === selectedAssignment.target_actor_id);
    if (!marker) return '';
    const labels = selectedAssignment.target_side === 'our' ? ourDisplayLabels : opponentDisplayLabels;
    return displayLabel(labels, marker);
  })();
  const progressionLabel = v => (v ? `Read ${v}` : 'No Read');

  // Phase B.3g (§29/§32/§33/§34) — per-cue display description for the
  // Top HUD's Passing Read list (up to three rows, one per
  // qbMomentPassingReads entry). Resolved for DISPLAY only, never
  // re-derived into the stored cue itself (§32: "flag as stale, never
  // silently retarget"). `missing` covers §33/§34 (target actor or its
  // route no longer exists); `stale` covers §32 (the target still
  // exists, but its OWN route progression no longer matches what this
  // cue was authored against — e.g. the coach later changed WR1's Read
  // from 1 to 2); `needsReadPoint` covers a genuinely legacy B.3e-shaped
  // cue with no captured `route_progress` at all (B.3e's own shape is a
  // strict subset of this one — see utils/playbook.js's own header note)
  // — the cue is NEVER auto-corrected or silently redirected in any of
  // these cases, only flagged, with Change/Remove still available.
  const describeMomentReadCue = (cue) => {
    const targetMarker = markersForSide(doc, cue.target_side).find(m => m.id === cue.target_actor_id);
    const targetAssignment = targetMarker
      ? assignments.find(a => a.actor_side === cue.target_side && a.actor_id === cue.target_actor_id)
      : null;
    const missing = !targetMarker || !targetAssignment;
    const stale = Boolean(targetMarker && targetAssignment && !eligibleReadTargets(cue.progression).some(m => m.id === targetMarker.id));
    const needsReadPoint = !missing && typeof cue.route_progress !== 'number';
    const label = targetMarker ? displayLabel(ourDisplayLabels, targetMarker) : null;
    return { targetMarker, missing, stale, needsReadPoint, label };
  };

  // Phase B.3h (§11/§12/§13/§14) — Passing Read status/edit belongs in
  // the TOP-RIGHT of the responsibility toolbar, not its own vertical
  // section (§12/§13). `passingReadStatusChip` is the compact "READ 3 →
  // WR3" text (right side of the HEADER row); `passingReadEditControls`
  // is the matching "[Edit Read] [Remove]" pair (right side of the
  // BUTTON row, same idle-resting state). Both are `null` — rendering
  // nothing extra — whenever a read is being actively added/changed
  // (the eligible-target row below takes over instead) or none exists
  // yet (the "no-read" eligible-buttons row, §15, is the only Passing
  // Read UI shown in that case — no separate status chip to show). Built
  // once here and reused at both post-snap header/button sites below
  // (resting-with-assignment AND the no-assignment-yet palette) so a QB
  // with or without a responsibility of their own still sees their
  // Passing Read status consistently.
  const showPassingReadInHeader = Boolean(
    fieldMode === 'select' && !pendingType && isSelectedOurQB && atPostSnap
    && selectedMoment && changingReadProgression === null && qbMomentPassingReads.length > 0,
  );
  const passingReadStatusChip = showPassingReadInHeader ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {qbMomentPassingReads.map(cue => {
        const info = describeMomentReadCue(cue);
        const flagged = info.stale || info.missing || info.needsReadPoint;
        return (
          <Typography key={cue.id} sx={{ color: flagged ? '#e0a73a' : '#8fb0f0', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            READ {cue.progression} → {info.missing ? 'unavailable' : info.label}
            {info.stale && ' · changed'}
            {info.needsReadPoint && ' · needs point'}
          </Typography>
        );
      })}
    </Box>
  ) : null;
  // Phase B.3h.4 (Task 4) — "Edit Read" → "Edit": the adjacent status
  // Typography already says "READ N → Target", so repeating "Read" in
  // the action label was redundant. Same handler, same behavior — label
  // only. Also lightened to match the Target/Redraw/Change treatment
  // (fontWeight 600, muted color) so these read as secondary actions
  // beside the bolder READ status text, not equal-weight competitors.
  const passingReadEditControls = showPassingReadInHeader ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {qbMomentPassingReads.map(cue => (
        <Box key={cue.id} sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={() => startChangeRead(cue.progression)} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, color: '#9eb8d0' }}>Edit</Button>
          <Button size="small" color="error" onClick={() => removeMomentPassingRead(selectedMoment, cue.progression)} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>Remove</Button>
        </Box>
      ))}
    </Box>
  ) : null;

  // Phase B.4a — THREAT status/edit controls, mirroring the Passing Read
  // pair immediately above byte-for-byte in structure (same gating shape,
  // same Row 1 right-cluster slot, same Edit/Remove action pair) but for
  // the brand-new 'threat' cue type and the offensive-owner-only gate
  // (`isSelectedOurOffense`/`selectedMoment`, not the QB-only
  // `isSelectedOurQB`). A cue is "missing" (target defender no longer
  // resolves) the same tolerant way describeMomentReadCue already flags
  // stale Passing Read targets — no separate load-time validator needed;
  // this display-time check IS the validation the phase spec asked for,
  // consistent with the existing "tolerant read, flag at display" house
  // style already used for Passing Read.
  const describeMomentThreatCue = (cue) => {
    const targetMarker = markersForSide(doc, cue.target_side).find(m => m.id === cue.target_actor_id);
    const labels = cue.target_side === 'our' ? ourDisplayLabels : opponentDisplayLabels;
    const label = targetMarker ? displayLabel(labels, targetMarker) : null;
    return { targetMarker, missing: !targetMarker, label };
  };

  const showThreatControls = Boolean(fieldMode === 'select' && !pendingType && isSelectedOurOffense && selectedMoment);
  const threatCue = showThreatControls
    ? getMomentCues(selectedMoment).find(c => c.type === 'threat' && c.actor_side === selected.side && c.actor_id === selected.id) || null
    : null;
  const threatStatusChip = (showThreatControls && threatCue) ? (
    (() => {
      const info = describeMomentThreatCue(threatCue);
      return (
        <Typography sx={{ color: info.missing ? '#e0a73a' : '#e0483a', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          THREAT → {info.missing ? 'unavailable' : info.label}
        </Typography>
      );
    })()
  ) : null;
  const threatEditControls = showThreatControls ? (
    threatCue ? (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button size="small" onClick={startThreatSelect} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, color: '#9eb8d0' }}>Edit</Button>
        <Button size="small" color="error" onClick={removeMomentThreat} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>Remove</Button>
      </Box>
    ) : (
      <Button size="small" onClick={startThreatSelect} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, color: '#e0483a' }}>+ Threat</Button>
    )
  ) : null;

  // MOMENT POSITION derived values (§16 of the B.2.3 unified-editor
  // pass) — deliberately a SEPARATE data model from selectedAssignment/
  // RESPONSIBILITY above (see this file's own note in the merged panel
  // below on why they're never combined): this describes WHERE the
  // selected actor is at the CURRENT scrub position/Moment, not WHAT
  // they're responsible for.
  const hasPathForMoment = Boolean(selectedAssignment && isPathBasedTimelineType(normalizeAssignmentType(selectedAssignment)));
  const atMarkedPoint = Boolean(selectedKeyframe);
  const momentState = (selected && atMarkedPoint)
    ? getActorKeyframeState(timeline, selected.side, selected.id, selectedKeyframe.id)
    : null;
  // Renamed from the pre-B.3 `isAtSnap` (now the name of an imported
  // playbookLifecycle.js helper operating on `fullScrub` instead) —
  // same exact meaning as before: is the currently-SELECTED Moment
  // specifically the reserved Snap keyframe. Only ever meaningful within
  // the Post-Snap Top HUD branch (Pre-Snap has no `kind:'snap'`
  // keyframe at all).
  const isSnapKeyframe = selectedKeyframe?.kind === 'snap';
  const momentProgressPct = atMarkedPoint
    ? Math.round((typeof momentState?.progress === 'number' ? momentState.progress : selectedKeyframe.position) * 100)
    : 0;

  let fieldInstruction = null;
  if (fieldMode === 'draw') {
    if (drawingPath.length > 0) fieldInstruction = 'Tap another point or Finish.';
    else if (assignmentType === 'route') fieldInstruction = `ROUTE (${progressionLabel(progressionChoice)}) — tap the field to add ${selectedLabel}'s route points.`;
    else if (assignmentType === 'movement') fieldInstruction = `MOVEMENT — tap the field to add ${selectedLabel}'s movement path.`;
    else if (assignmentType === 'carry') fieldInstruction = `RUN — tap the field to add ${selectedLabel}'s run path.`;
    else if (assignmentType === 'rush') fieldInstruction = `PASS RUSH — tap the field to add ${selectedLabel}'s rush path.`;
    else if (assignmentType === 'blitz') fieldInstruction = `BLITZ — tap the field to add ${selectedLabel}'s blitz path.`;
    else if (assignmentType === 'run_fit') fieldInstruction = `RUN FIT — tap the field to add ${selectedLabel}'s run-fit path.`;
    else if (assignmentType === 'block' || assignmentType === 'run_block') fieldInstruction = `BLOCK — tap the field to add ${selectedLabel}'s block path.`;
    else if (assignmentType === 'pass_block') fieldInstruction = `PASS BLOCK — tap the field to redraw ${selectedLabel}'s set.`;
    else if (assignmentType === 'handoff') fieldInstruction = `HANDOFF to ${recipientLabel} — tap the field to add ${selectedLabel}'s exchange path.`;
    else if (assignmentType === 'pass') fieldInstruction = `PASS to ${recipientLabel} — tap the field to add ${selectedLabel}'s drop/release path.`;
  } else if (fieldMode === 'zone') {
    fieldInstruction = `ZONE — tap the center of ${selectedLabel}'s coverage area.`;
  }

  // Compact one-line summary for the existing-assignment view (§43,
  // strengthened in B.3h.4 Task 1) — never used for persistence, purely
  // a display string. Combined with {selectedLabel} as
  // "{selectedLabel} · {existingSummary}" (e.g. "QB · PASS → WR1"), this
  // is meant to read as ONE strong actor/responsibility/target anchor —
  // the single most important piece of state in the Top HUD, uppercased
  // and arrow-joined so it visually outweighs the action buttons beside
  // it (§Task 1/§Visual Hierarchy: "should read immediately as ONE
  // summary... keep visually stronger than the surrounding edit
  // commands"). "→" replaces the old "· to"/"· has"/"· targeting"
  // phrasing wherever there's a real target; the no-target/no-recipient
  // cases keep plain " · " phrasing since there's nothing to arrow
  // toward.
  const existingSummary = (() => {
    if (!selectedAssignment) return '';
    const t = normalizeAssignmentType(selectedAssignment);
    const typeLabel = (ASSIGNMENT_TYPE_META[t]?.label || t).toUpperCase();
    if (t === 'route') return `${typeLabel} · ${progressionLabel(selectedAssignment.progression)}`;
    if (t === 'man') return targetLabel ? `${typeLabel} → ${targetLabel}` : `${typeLabel} · no target`;
    if (t === 'handoff') return recipientLabel ? `${typeLabel} → ${recipientLabel}` : `${typeLabel} · no recipient`;
    if (t === 'pass') return recipientLabel ? `${typeLabel} → ${recipientLabel}` : `${typeLabel} · no recipient`;
    if (BLOCK_FAMILY_TYPES.has(t)) return legacyBlockTargetLabel ? `${typeLabel} → ${legacyBlockTargetLabel}` : typeLabel;
    return typeLabel;
  })();
  // B.2.4b (§8/§9) — true only in the idle "resting" state: a committed
  // assignment exists, the coach isn't mid-Change-Responsibility, and no
  // draw/zone/pendingType flow is active. Used purely to decide whether
  // the muted "OUR WR2" header line collapses INTO the bolder responsibility
  // line below it (one line instead of two) — never changes what data is
  // read/written, only which Typography renders it.
  const restingWithAssignment = Boolean(selectedAssignment && !changingResponsibility && fieldMode === 'select' && !pendingType);
  // Phase B.3h.3 (§Row 1 condensation) — the OTHER idle "resting" state:
  // no committed assignment yet (or the coach explicitly tapped
  // "Change"), still fieldMode==='select' with nothing pending — i.e.
  // exactly the final `else` branch of the Post-Snap responsibility
  // ternary below (the primary-action palette). Used to fold the plain
  // `{selectedLabel}` header line INTO that palette's own row (§Row 1:
  // "related controls grouped horizontally instead of stacked into
  // separate rows") instead of rendering it as a separate row above —
  // mirrors restingWithAssignment's own "collapse the header into the
  // content line" reasoning, just for the no-assignment case.
  const showingPalette = Boolean(fieldMode === 'select' && !pendingType && !(selectedAssignment && !changingResponsibility));

  // ───────────────────────────────────────────────────────────────────
  // Phase B.2 continuous-scrubber correction — PTP timeline position
  // resolution. Resolves ONE actor's FIELD-native (never screen-pixel)
  // position at an ARBITRARY continuous scrub position (0..1), not a
  // keyframe array index. Reuses assignmentScreenPoints() as-is (so
  // Block-family's existing contact-point pullback — blockContactPoint()
  // — applies for free) and pointAlongPolyline() for arc-length-correct
  // walking (see that function's own header note on why this has to
  // happen in SCREEN space, then convert back).
  //
  // bracketKeyframesForPosition() finds the two keyframes adjacent to
  // `position` (or a single exact match within tolerance — see that
  // function's own note). Three cases:
  //   1. Exact match (a === b) — resolve that ONE keyframe directly:
  //      override wins, else progress-along-path at its explicit
  //      progress or (default) its own `position`, else the actor's
  //      static starting marker.
  //   2. Between two keyframes, NEITHER side has an override, and the
  //      actor has a path-based assignment — blend the two sides'
  //      PROGRESS values (explicit or default-to-position) by `frac`
  //      and walk the path at that ONE blended progress. This is what
  //      keeps a corner route visibly following its own corner
  //      throughout a scrub (not just snapping correctly at marked
  //      points) AND is exactly what makes "default progress = scrub
  //      position" true by construction: when both bracket points are
  //      unmodified defaults, their progress values equal their own
  //      `position`, and blending two positions by the same fraction
  //      used to reach `position` between them algebraically reproduces
  //      `position` itself (see §7/§V of the report).
  //   3. Any other case — an override is involved on either side, or
  //      the actor has no path at all. There is no shared "progress"
  //      domain to blend an override into, so this falls back to the
  //      simplest safe behavior (§6/§Z of the report): resolve BOTH
  //      bracket keyframes to their own fully-resolved screen point
  //      (override, or progress-along-path, or the static marker for a
  //      non-path actor) and straight-line lerp between those two
  //      points by `frac`.
  // Phase B.3 (§12) generalization — the exact same bracket/override/
  // progress-blend logic as before, now parameterized by `startPoint`
  // (the actor's position at THIS PHASE's own progress===0 — §11's
  // "effectiveStart") and by which phase-local keyframe list/assignment
  // pool to walk, so ONE resolver serves BOTH Pre-Snap and Post-Snap —
  // never two parallel copies (§12: "do not create one resolver for
  // moving players and another for static players"). Declared BEFORE
  // drawingPreviewPoints below (which calls resolveSnapPosition()) so
  // its own `const preSnapResolutionKeyframes` dependency is guaranteed
  // already initialized — `function` hoisting alone is not enough here,
  // since the FUNCTION BODY reads a `const` that must have already run.
  // Phase B.3f (§21) — now returns `{ position, pathProgress }` instead
  // of a bare position. `pathProgress` is the SAME internal `t` this
  // function already computed to walk the actor's path — previously
  // discarded, now also surfaced directly so a route-attached feature
  // (Passing Read windows) never has to reverse-engineer "how far along
  // their route is this actor" from x/y (§21's own explicit preference:
  // "prefer one canonical helper... do not duplicate the full position
  // resolver"). `pathProgress` is `null` whenever there is no path to
  // report one for (a legacy override resolved exactly on its own
  // keyframe, or a non-path actor). Every existing caller is updated to
  // unwrap `.position` — the POSITION half of this contract, including
  // the HB1 bug fix reasoning below, is otherwise 100% unchanged.
  function resolvePhaseLocalPosition({ side, id, startPoint, keyframes, assignment, progress }) {
    const toField = side === 'our' ? screenToField : opponentScreenToField;
    const toScreen = side === 'our' ? fieldToScreen : opponentFieldToScreen;
    const normType = assignment ? normalizeAssignmentType(assignment) : null;
    const hasPath = Boolean(assignment && isPathBasedTimelineType(normType) && assignment.path && assignment.path.length > 0);
    // Phase B.3h.1 (§1/§4) — REUSE the already-memoized point array for
    // this exact assignment (postSnapPathElements/preSnapMotionElements,
    // via their own postSnapPathPointsById/preSnapMotionPointsById
    // lookup maps) instead of recomputing assignmentScreenPoints() from
    // scratch every time this resolver runs — this function is invoked
    // once per actor on EVERY resolveActorAtFullScrub() call (i.e. once
    // per marker per playback tick), so recomputing an identical
    // point-array walk here was pure redundant work, duplicating a
    // computation this component already does once per doc-change
    // elsewhere.
    //
    // BUG FIX (B.3h.2a — TDZ crash) — this lookup MUST be type-aware,
    // never "try postSnapPathPointsById, then preSnapMotionPointsById"
    // unconditionally. `resolveSnapPosition()`'s own Pre-Snap Motion
    // branch calls THIS function with a MOTION assignment — and
    // `postSnapPathElements`'s own useMemo initializer calls
    // resolveSnapPosition() (to compute each post-snap assignment's
    // effective start) WHILE `postSnapPathPointsById` (built FROM
    // postSnapPathElements, declared AFTER it) has not been assigned
    // yet. Merely REFERENCING `postSnapPathPointsById` at that point —
    // even just to call `.get()` on it, even though the lookup would
    // have correctly missed — throws a TDZ ReferenceError, because a
    // `const` binding cannot be touched at all before its own
    // initializer has run, regardless of what the code would have done
    // with the result. The old code tried postSnapPathPointsById FIRST
    // unconditionally, so ANY Play containing an actor with BOTH
    // Pre-Snap Motion AND a Post-Snap path-based responsibility crashed
    // on first render — not specific to "motion returns to its
    // starting coordinate" at all (a same-start/end path never
    // mattered; path EXISTENCE did, exactly as required below).
    //
    // Fix: resolve which SINGLE map is even relevant from `normType`
    // (already computed above) BEFORE touching either binding. A
    // 'motion' assignment (Pre-Snap) only ever needs
    // preSnapMotionPointsById, which has ZERO dependency on
    // postSnapPathElements/postSnapPathPointsById — so resolving Motion
    // (from anywhere, including from inside postSnapPathElements' own
    // computation) never references postSnapPathPointsById at all,
    // fully breaking the cycle. A non-'motion' (Post-Snap) assignment
    // only ever needs postSnapPathPointsById, which by construction is
    // only ever consulted from call sites that run AFTER it (renderDoc,
    // resolveActorRouteProgressAtFullScrub — both outside
    // postSnapPathElements' own initializer). Falls back to a fresh
    // computation only if the assignment genuinely isn't in its own map
    // (defensive; should not occur in practice since both maps are
    // always rebuilt from the SAME `assignments`/`preSnapAssignments`
    // arrays this function's own `assignment` argument always comes
    // from).
    const pts = hasPath
      ? (normType === 'motion'
          ? (preSnapMotionPointsById.get(assignment.id) || assignmentScreenPoints(doc, assignment, startPoint))
          : (postSnapPathPointsById.get(assignment.id) || assignmentScreenPoints(doc, assignment, startPoint)))
      : null;

    // Fully resolves ONE keyframe to a SCREEN point (+ path-progress, if
    // any) for this actor — override > progress-along-path (explicit or
    // default-to-position) > the phase's own static startPoint (non-path
    // actor).
    //
    // BUG FIX (post-B.3d investigation) — Snap is now UNCONDITIONALLY
    // immune to `state?.override`, even a legacy one. No current UI path
    // can ever write actor_state keyed to the reserved Snap keyframe's
    // id (the old "Set Custom Position" panel was always replaced by a
    // collapsed, non-interactive line whenever `isSnapKeyframe` was
    // true, all the way back to B.2.4b) — this is defensive hardening
    // of an invariant that was previously only a UI-level guarantee,
    // never enforced at the resolver itself. "Snap position is
    // authoritative at Snap" now holds unconditionally, with no
    // legacy-data exception (unlike ordinary Post-Snap actors, where a
    // legacy override remains an intentional, honored exception).
    const resolveOneScreen = (kf) => {
      const state = getActorKeyframeState(timeline, side, id, kf.id);
      if (kf.kind !== 'snap' && state?.override) {
        return { screen: toScreen(doc.field_config, state.override.x, state.override.y), pathProgress: null };
      }
      if (hasPath && pts && pts.length > 0) {
        const t = typeof state?.progress === 'number' ? state.progress : kf.position;
        return { screen: pointAlongPolyline(pts, t), pathProgress: t };
      }
      return { screen: toScreen(doc.field_config, startPoint.x, startPoint.y), pathProgress: null };
    };

    const bracket = bracketKeyframesForPosition(keyframes, progress);
    if (!bracket) {
      // Defensive only — Post-Snap always has real Snap(0)/End(1), and
      // Pre-Snap resolution always synthesizes its own 0/1 boundary
      // keyframes (see resolveActorAtFullScrub/resolveSnapPosition
      // below), so `keyframes` is never actually empty in practice.
      if (hasPath && pts && pts.length > 0) {
        const screenPt = pointAlongPolyline(pts, progress);
        return { position: toField(doc.field_config, screenPt.px, screenPt.py), pathProgress: progress };
      }
      return { position: startPoint, pathProgress: null };
    }

    if (bracket.a.id === bracket.b.id) {
      const resolved = resolveOneScreen(bracket.a);
      return { position: toField(doc.field_config, resolved.screen.px, resolved.screen.py), pathProgress: resolved.pathProgress };
    }

    // BUG FIX (post-B.3d investigation, root cause) — this branch used
    // to REQUIRE both bracket keyframes to be override-free before it
    // would walk the actor's own path; if EITHER side had a legacy
    // `override` (from the pre-B.3c "Set Custom Position" workflow),
    // resolution fell through to the screenA/screenB straight-LINE
    // blend below instead — a screen-space lerp from the path's start
    // point STRAIGHT TOWARD the arbitrary legacy XY, weighted by
    // `bracket.frac`. Because `frac` is small-but-nonzero for any scrub
    // position merely NEAR (not exactly on) an overridden keyframe, this
    // pulled the actor visibly off their authored path well before
    // reaching it — including scrub positions very close to Snap, when
    // the overridden keyframe was a Moment early in the play. That
    // matches the reported symptom exactly (HB1 displaced from his route
    // near the Snap boundary).
    //
    // Fix: whenever the actor HAS an authored path, ALWAYS interpolate
    // ALONG that path — a legacy override contributes no `.progress`
    // field, so it now simply falls back to that keyframe's own
    // timeline `position` as its effective path-progress for
    // interpolation purposes (exactly like an un-overridden default
    // would). The override itself is NOT discarded — resolveOneScreen()
    // above still returns it verbatim the instant the scrub sits EXACTLY
    // on that keyframe (the `bracket.a.id === bracket.b.id` branch,
    // untouched) — so legacy data remains fully load-compatible and
    // visible at its own exact Moment, it just no longer leaks its
    // influence into neighboring scrub positions the way a straight-line
    // blend did. The screenA/screenB fallback below is now reached ONLY
    // when the actor has NO authored path at all (Man/Zone/no
    // assignment) — completely unchanged for that case.
    const stateA = getActorKeyframeState(timeline, side, id, bracket.a.id);
    const stateB = getActorKeyframeState(timeline, side, id, bracket.b.id);
    if (hasPath && pts && pts.length > 0) {
      const progA = typeof stateA?.progress === 'number' ? stateA.progress : bracket.a.position;
      const progB = typeof stateB?.progress === 'number' ? stateB.progress : bracket.b.position;
      const t = progA + (progB - progA) * bracket.frac;
      const screenPt = pointAlongPolyline(pts, t);
      return { position: toField(doc.field_config, screenPt.px, screenPt.py), pathProgress: t };
    }

    const screenA = resolveOneScreen(bracket.a).screen;
    const screenB = resolveOneScreen(bracket.b).screen;
    const blended = {
      px: screenA.px + (screenB.px - screenA.px) * bracket.frac,
      py: screenA.py + (screenB.py - screenA.py) * bracket.frac,
    };
    return { position: toField(doc.field_config, blended.px, blended.py), pathProgress: null };
  }

  // Pre-Snap has no REAL stored Formation/Snap-boundary keyframes (only
  // coach-created Moments ever live in `preSnapKeyframesSorted` — see
  // this file's own header note on why Formation/Snap don't need their
  // own persisted keyframes the way Post-Snap's Snap/End do). These two
  // SYNTHETIC, never-persisted boundary keyframes give
  // resolvePhaseLocalPosition() the same "always >= 2 keyframes to
  // bracket between" guarantee Post-Snap's real Snap/End already
  // provide, letting Pre-Snap reuse the IDENTICAL bracket/blend engine
  // with zero special-casing. Their ids are fixed constants that can
  // never collide with a real `newMarkerId()`-generated id, so
  // getActorKeyframeState() against them always correctly misses
  // (no actor_state is ever — or can ever be — stored against a
  // synthetic id), which is exactly the desired "no override at the
  // Formation/Snap boundary" default.
  const preSnapResolutionKeyframes = [
    { id: '__formation_boundary__', kind: 'boundary', position: 0 },
    ...preSnapKeyframesSorted,
    { id: '__pre_snap_snap_boundary__', kind: 'boundary', position: 1 },
  ];

  /** The actor's fully-resolved position AT SNAP (§10 of B.3, precedence
   * extended by §5/§11 of B.3b): explicit Pre-Snap Motion endpoint, ELSE
   * a static Pre-Snap set-position override, ELSE plain Formation
   * `marker.start`. A FIXED value for a given actor+document,
   * independent of the coach's current fullScrub. This ONE canonical
   * helper is the only place that precedence is decided — every
   * downstream consumer (renderDoc via resolveActorAtFullScrub below,
   * and every Post-Snap assignment's effective start at render time)
   * goes through it, so there is never a second competing
   * implementation of "where is this actor once Pre-Snap is over" (§11:
   * "do not scatter precedence logic throughout rendering"). A Motion
   * actor's set_positions entry (if any stale one exists — see
   * finishDrawing()'s own cleanup when Motion is first authored) is
   * NEVER consulted here — Motion always wins outright when present,
   * exactly per §5's "a player with explicit Motion should NOT also
   * require/use an independent static set-position override." */
  function resolveSnapPosition(side, id) {
    const marker = markersForSide(doc, side).find(m => m.id === id);
    if (!marker) return null;
    const preSnapAssignment = preSnapAssignments.find(a => a.actor_side === side && a.actor_id === id);
    if (preSnapAssignment && preSnapAssignment.path && preSnapAssignment.path.length > 0) {
      return resolvePhaseLocalPosition({
        side, id, startPoint: marker.start,
        keyframes: preSnapResolutionKeyframes, assignment: preSnapAssignment, progress: 1,
      }).position;
    }
    const setPosition = findPreSnapSetPosition(side, id);
    if (setPosition) return setPosition;
    return marker.start;
  }

  /** The ONE phase-dispatching entry point every render-time position
   * lookup goes through (renderDoc below, and the per-actor `snapPos`
   * used to give Post-Snap assignment artwork its correct effective
   * start) — resolves an actor's field-native position at an ARBITRARY
   * point on the FULL lifecycle scrubber, dispatching to
   * resolvePhaseLocalPosition() with the correct phase's own
   * keyframes/assignment pool/startPoint. This is the direct successor
   * to the pre-B.3 `resolveActorAtScrub()` — same continuous-scrub
   * contract, now spanning the whole lifecycle instead of only
   * Post-Snap.
   *
   * Phase B.3b (§38) — a NON-Motion actor with a static set-position
   * override renders as a clean STEP, never an interpolated/animated
   * transition: exactly at Formation (progress===0) they show at
   * Formation; anywhere else in Pre-Snap (progress>0, all the way
   * through Snap and beyond) they show at the set position. This is a
   * deliberate choice (documented explicitly, per §38's own
   * requirement) — an animated slide from Formation to the set position
   * would visually read as unlabeled Motion, which §3/§38 both
   * explicitly forbid ("do not invent a visible Motion path for the
   * static adjustment"); a step avoids that ambiguity entirely rather
   * than merely softening it. */
  function resolveActorAtFullScrub(side, id, fullScrubValue) {
    const marker = markersForSide(doc, side).find(m => m.id === id);
    if (!marker) return null;
    const { phase: p, progress } = fullScrubToLifecycle(fullScrubValue);
    if (p === 'pre_snap') {
      const preSnapAssignment = preSnapAssignments.find(a => a.actor_side === side && a.actor_id === id);
      if (preSnapAssignment && preSnapAssignment.path && preSnapAssignment.path.length > 0) {
        return resolvePhaseLocalPosition({
          side, id, startPoint: marker.start,
          keyframes: preSnapResolutionKeyframes, assignment: preSnapAssignment, progress,
        }).position;
      }
      const setPosition = findPreSnapSetPosition(side, id);
      if (setPosition) return progress > 0 ? setPosition : marker.start;
      return marker.start;
    }
    const snapPosition = resolveSnapPosition(side, id);
    const assignment = assignments.find(a => a.actor_side === side && a.actor_id === id);
    return resolvePhaseLocalPosition({
      side, id, startPoint: snapPosition || marker.start,
      keyframes: postSnapKeyframesSorted, assignment, progress,
    }).position;
  }

  /** Phase B.3f (§21) — the actor's resolved PATH PROGRESS (0..1 along
   * their OWN authored Post-Snap path) at an arbitrary point on the full
   * lifecycle scrubber — the `pathProgress` half of
   * resolvePhaseLocalPosition()'s own return value, surfaced directly
   * rather than reverse-engineered from x/y. Returns `null` when there
   * is no route progress to report: Pre-Snap (Passing Read is Post-Snap
   * only — see §38 of the B.3f spec; this function deliberately never
   * even evaluates the Pre-Snap branch), a legacy override resolved
   * exactly on its own keyframe, or an actor with no path-based
   * assignment at all (Man/Zone/no assignment). A thin wrapper, NOT a
   * second resolver — reuses the exact same resolveSnapPosition()/
   * resolvePhaseLocalPosition() call resolveActorAtFullScrub() itself
   * makes for the position half of this same computation (§21: "do not
   * duplicate the full position resolver"). */
  function resolveActorRouteProgressAtFullScrub(side, id, fullScrubValue) {
    const marker = markersForSide(doc, side).find(m => m.id === id);
    if (!marker) return null;
    const { phase: p, progress } = fullScrubToLifecycle(fullScrubValue);
    if (p === 'pre_snap') return null;
    const snapPosition = resolveSnapPosition(side, id);
    const assignment = assignments.find(a => a.actor_side === side && a.actor_id === id);
    return resolvePhaseLocalPosition({
      side, id, startPoint: snapPosition || marker.start,
      keyframes: postSnapKeyframesSorted, assignment, progress,
    }).pathProgress;
  }

  // Phase B.3h.1 — moved to occur AFTER postSnapPathElements/
  // preSnapMotionElements/their *PointsById maps/renderDoc below
  // (formerly sat here, BEFORE them) — this block calls
  // resolveSnapPosition(), which can call resolvePhaseLocalPosition(),
  // which (as of B.3h.1) reads postSnapPathPointsById/
  // preSnapMotionPointsById; those consts must already be initialized
  // (not just hoisted-as-declared, since they're `const`) by the time
  // any code path actually CALLS resolveSnapPosition, or this throws a
  // "Cannot access before initialization" TDZ error the instant a coach
  // starts drawing while any Motion assignment exists anywhere in the
  // Play. See drawingPreviewPoints' own new location, just after
  // renderDoc below, for the actual computation (unchanged in substance,
  // only in position).

  // Phase B.3g (§39-§46) — assignment polylines and Zone geometry are
  // drawn from the REAL `doc` and stay "unmoving" during a scrub — i.e.
  // they are PROVABLY pure functions of `doc` alone, never of
  // `fullScrub`. Despite that, before B.3g they were being fully
  // RECOMPUTED from scratch (assignmentScreenPoints' own point-array
  // walk, resolveSnapPosition's own path/keyframe resolution) on every
  // single one of the ~60 setFullScrub calls/sec usePlaybackAnimation's
  // rAF loop drives during Play — for every authored Route/Block/Rush/
  // Motion assignment, none of which had actually changed. Memoizing the
  // pieces that are provably pure functions of `doc` removed that dead
  // work from every playback frame. The small number of Zone-arrow/
  // Man-connection elements whose START point also legitimately follows
  // a resolved position are kept inline in the render below, unchanged
  // — cheap single-segment geometry, not a meaningful cost.
  const postSnapZoneElements = React.useMemo(() => assignments
    .filter(a => normalizeAssignmentType(a) === 'zone')
    .map(a => {
      const geo = zoneScreenGeometry(doc, a); // zone center/radius never move (§16 of B.3d) — real doc, not renderDoc
      return geo ? { id: a.id, center: geo.center, radiusPx: geo.radiusPx } : null;
    })
    .filter(Boolean), [assignments, doc]);

  const preSnapMotionElements = React.useMemo(() => preSnapAssignments
    .map(a => {
      // Always drawn from the actor's raw Formation `doc` start
      // (effectiveStart omitted — Pre-Snap has no "start of start" to
      // resolve), never renderDoc.
      const pts = assignmentScreenPoints(doc, a);
      return pts && pts.length >= 2 ? { id: a.id, points: pts } : null;
    })
    .filter(Boolean), [preSnapAssignments, doc]);

  // Phase B.3h.1 (§1/§4) — PLAYBACK CLOCK REPAIR. Investigation (see this
  // file's own final report) proved via isolated tests that
  // usePlaybackAnimation.js's rAF/state pipeline emits and commits every
  // intermediate `fullScrub` value correctly and smoothly — the clock
  // itself was never the bug. B.3h's own `fieldFullScrub` quantization
  // (REMOVED this pass — see renderDoc's own note below) was a step in
  // the right direction but partly masked, rather than fixed, the real
  // remaining cost: `resolvePhaseLocalPosition()` (called once per actor
  // per tick via resolveActorAtFullScrub, for renderDoc below) was
  // calling `assignmentScreenPoints()` AGAIN, from scratch, to walk each
  // path-based actor's polyline — genuinely REDUNDANT with the exact
  // same computation this memo (and postSnapPathElements below) already
  // does. `preSnapMotionPointsById`/`postSnapPathPointsById` (this one
  // and its sibling right after postSnapPathElements) let
  // resolvePhaseLocalPosition() REUSE these already-memoized point
  // arrays by assignment id instead of recomputing them — same
  // computation, reused rather than duplicated, no approximation, no
  // quantization, no new clock. Declared here (before postSnapPathElements)
  // because postSnapPathElements' own effectiveStart computation
  // (resolveSnapPosition) resolves a Motion actor's Pre-Snap endpoint via
  // resolvePhaseLocalPosition, which needs this map already built.
  const preSnapMotionPointsById = React.useMemo(() => {
    const m = new Map();
    for (const el of preSnapMotionElements) m.set(el.id, el.points);
    return m;
  }, [preSnapMotionElements]);

  const postSnapPathElements = React.useMemo(() => assignments
    .filter(a => { const t = normalizeAssignmentType(a); return t !== 'zone' && t !== 'man'; })
    .map(a => {
      // Full authored path — always the REAL doc, never renderDoc: the
      // whole route/block/etc. stays visible and unmoving while
      // scrubbing (§32 of B.3 "assignment artwork may remain visible").
      // Its IMPLICIT first point is the actor's resolved SNAP position
      // (Formation if no Pre-Snap motion), same as before this pass —
      // only WHEN it recomputes changed, not what it computes.
      const normType = normalizeAssignmentType(a);
      const effectiveStart = resolveSnapPosition(a.actor_side, a.actor_id);
      const pts = assignmentScreenPoints(doc, a, effectiveStart);
      return pts && pts.length >= 2 ? { id: a.id, points: pts, type: normType, progression: a.progression } : null;
    })
    // resolveSnapPosition is a plain function declaration (a new
    // reference every render, like every other helper in this
    // component), not a useCallback; its own real inputs are
    // preSnapAssignments/preSnapSetPositions/doc, all already listed
    // below, so this correctly skips recompute across fullScrub-only
    // re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    .filter(Boolean), [assignments, doc, preSnapAssignments, preSnapSetPositions]);

  // Phase B.3h.1 — sibling to preSnapMotionPointsById above; see that
  // constant's own note for the full rationale.
  const postSnapPathPointsById = React.useMemo(() => {
    const m = new Map();
    for (const el of postSnapPathElements) m.set(el.id, el.points);
    return m;
  }, [postSnapPathElements]);

  // A single per-render map of every marker's RESOLVED lifecycle
  // position at the current fullScrub, then a `renderDoc` wrapper whose
  // marker `start` fields are swapped for those resolved positions —
  // every existing render helper that already reads
  // `markersForSide(doc, side).find(...).start` (marker circles,
  // manConnectionScreenPoints, zoneArrowScreenPoints) picks up resolved
  // positions with ZERO changes to that helper's own logic. Deliberately
  // NOT applied to the authored assignment polylines themselves (still
  // rendered from the real `doc`, full brightness, unmoving — "assignment
  // artwork may remain visible") or to the Zone circle's own center/
  // radius (fixed) — only to whichever actor's CURRENT position
  // something else is computed relative to.
  //
  // Phase B.2.3: computed UNCONDITIONALLY — there is no mode to gate it
  // on. At fullScrub === FORMATION_FULL_SCRUB this resolves to (modulo
  // an imperceptible floating-point round-trip through screen space)
  // exactly the actor's own stored `start`, which is exactly why
  // starting-alignment dragging is safe to restrict to Formation (see
  // handlePointerDown above) — the rendered marker IS the stored
  // position at that moment, not a derived one.
  //
  // Phase B.3h.1 (§4/§9) — B.3h's own `fieldFullScrub` quantization
  // (rounding fullScrub to the nearest SCRUB_STEP before resolving
  // marker positions) is REMOVED this pass. It reduced CPU cost, but at
  // the price of visibly coarsening the FIELD's own motion — the
  // Pre-Snap phase spans only 25% of the 0..1 range, so a 1% quantization
  // step meant just ~25 distinct field positions across its entire
  // 2800ms presentation duration (≈9 distinct updates/sec) — this is very
  // likely the direct cause of the "still visibly jumpy in other cases"
  // report, and it never actually addressed the deeper "freezes
  // completely, then jumps to End" symptom (which isolated testing of
  // usePlaybackAnimation.js proved is NOT a clock/state-delivery bug —
  // see this file's own final report). `renderDoc` now resolves against
  // the exact, unrounded `fullScrub` again — full precision, matching
  // manual-scrub accuracy exactly, no approximation. The real per-tick
  // cost this was papering over is instead reduced directly, via
  // preSnapMotionPointsById/postSnapPathPointsById above (resolveActorAtFullScrub
  // → resolvePhaseLocalPosition no longer redundantly recomputes each
  // path's point array from scratch on every tick — see those constants'
  // own note). This still-recomputes-every-tick by design (§6 of the
  // B.3h.1 spec: "do not throttle authoritative fullScrub... do not add
  // more quantization") — kept as a `useMemo` purely as a harmless,
  // zero-risk guard against redundant recompute on a re-render where
  // `fullScrub` itself did NOT change (e.g. an unrelated Top-HUD state
  // update while paused), not as a playback-time throttle.
  const renderDoc = React.useMemo(() => ({
    ...doc,
    our: { ...doc.our, markers: doc.our.markers.map(m => ({ ...m, start: resolveActorAtFullScrub('our', m.id, fullScrub) || m.start })) },
    opponent: { ...doc.opponent, markers: doc.opponent.markers.map(m => ({ ...m, start: resolveActorAtFullScrub('opponent', m.id, fullScrub) || m.start })) },
    // resolveActorAtFullScrub is a plain function declaration (a new
    // reference every render), not a useCallback; its own real inputs
    // are doc/timeline (both listed below), never anything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [doc, timeline, fullScrub]);

  // Live in-progress path preview while drawing — rendered separately
  // from committed assignments (dashed white) so it's visually obvious
  // it isn't saved yet. Phase B.3 (§33/§34): the preview's own START
  // point must match whichever phase is being authored — Formation for
  // Pre-Snap Motion (`actor.start` itself), or the actor's resolved
  // SNAP position for Post-Snap (so drawing visibly begins from where a
  // motioned player actually ends up, not their original Formation
  // spot). Phase B.3h.1 — relocated to AFTER postSnapPathPointsById/
  // preSnapMotionPointsById/renderDoc (see this file's own note at this
  // block's OLD location) — purely a reordering, zero behavior change.
  let drawingPreviewPoints = null;
  if (fieldMode === 'draw' && selected && drawingPath.length > 0) {
    const actor = markersForSide(doc, selected.side).find(m => m.id === selected.id);
    if (actor) {
      const transform = selected.side === 'our' ? fieldToScreen : opponentFieldToScreen;
      const startXY = phase === 'pre_snap' ? actor.start : (resolveSnapPosition(selected.side, selected.id) || actor.start);
      drawingPreviewPoints = [startXY, ...drawingPath].map(p => transform(doc.field_config, p.x, p.y));
    }
  }

  // Phase B.3g (§21/§26/§27/§28/§38) — which (if any) Passing Read cue is
  // currently "live," driven by MOMENT PROXIMITY (isMomentCueVisible() —
  // playbookLifecycle.js) rather than target route-progress window
  // membership (B.3f's own model — replaced, see that file's updated
  // header note on the history). Deliberately independent of the
  // CURRENT selection (a coach watching playback should see the cue
  // regardless of what's selected on screen). NEVER evaluated outside
  // `phase === 'post_snap'` (§38) — Snap/End are real keyframes but
  // never coach Moments, so `kind === 'keyframe'` already excludes them
  // without a separate check. §28 "overlap policy": among every Moment
  // currently within its own presentation window, the CLOSEST one to
  // current progress wins — postSnapKeyframesSorted is already
  // position-ascending and only a STRICT `<` replaces the running best,
  // so an exact tie keeps the earlier (lower-position) Moment,
  // deterministic tie-breaking for free. Within the winning Moment, §27
  // "lowest progression number wins" (Read 1 > Read 2 > Read 3) picks
  // among that ONE Moment's own pass_read cues — only cues carrying a
  // captured `route_progress` are eligible to be drawn (a genuinely
  // legacy cue missing one has nothing to draw a line to; it still
  // surfaces in the authoring list via qbMomentPassingReads/
  // describeMomentReadCue, just never as a visual cue — §33/§34/§66).
  let visiblePassingReadCue = null;
  if (phase === 'post_snap') {
    let bestDist = Infinity;
    for (const kf of postSnapKeyframesSorted) {
      if (kf.kind !== 'keyframe') continue;
      const cues = getMomentCues(kf).filter(c => c.type === 'pass_read' && typeof c.route_progress === 'number');
      if (cues.length === 0) continue;
      if (!isMomentCueVisible(kf.position, postSnapProgress)) continue;
      const dist = Math.abs(kf.position - postSnapProgress);
      if (dist >= bestDist) continue;
      bestDist = dist;
      visiblePassingReadCue = cues.reduce((best, c) => (best === null || c.progression < best.progression ? c : best), null);
    }
  }
  // Phase B.3h — memoized so this rides on renderDoc's own B.3h throttle
  // for free: passingReadCueScreenPoints() walks the target's route
  // polyline (pointAlongPolyline), real but modest work that's still
  // pure waste to repeat on every tick where neither the resolved doc
  // nor the currently-visible cue actually changed.
  // resolveSnapPosition, same rationale as postSnapPathElements above:
  // its real inputs (doc) are already listed below.
  const visiblePassingReadCuePoints = React.useMemo(() => (
    visiblePassingReadCue
      ? passingReadCueScreenPoints(doc, renderDoc, visiblePassingReadCue, resolveSnapPosition(visiblePassingReadCue.target_side, visiblePassingReadCue.target_actor_id))
      : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [doc, renderDoc, visiblePassingReadCue]);

  // Phase B.4a — same closest-Moment-wins visibility rule as Passing Read
  // above (isMomentCueVisible/postSnapKeyframesSorted, no new timing
  // system), but Threat deliberately collects EVERY threat cue on the
  // winning Moment rather than picking just one (§Interaction with
  // Passing Reads / acceptance test 4: "different offensive actors may
  // have different Threat cues at the same Moment... both valid
  // simultaneously") — there is no progression-number tiebreak to apply
  // here the way Passing Read's "lowest progression wins" does, since
  // ownership is already unique per actor (§One-Threat-per-owner rule).
  // Starts `null` (a stable primitive), not `[]` — a fresh empty-array
  // literal here would itself be a new reference every render and trip
  // react-hooks/exhaustive-deps on the useMemo below for changing
  // identity every time even when logically unchanged (unlike
  // visiblePassingReadCue's own `null` default just above, which has the
  // same shape for the same reason).
  let visibleThreatCues = null;
  if (phase === 'post_snap') {
    let bestDist = Infinity;
    for (const kf of postSnapKeyframesSorted) {
      if (kf.kind !== 'keyframe') continue;
      const cues = getMomentCues(kf).filter(c => c.type === 'threat');
      if (cues.length === 0) continue;
      if (!isMomentCueVisible(kf.position, postSnapProgress)) continue;
      const dist = Math.abs(kf.position - postSnapProgress);
      if (dist >= bestDist) continue;
      bestDist = dist;
      visibleThreatCues = cues;
    }
  }
  // The halo is drawn at the TARGET DEFENDER's current resolved position
  // (renderDoc — same marker-position source the marker-circle loops
  // below already use), NOT a route polyline point like Passing Read's
  // line — a Threat has no route to walk, just a defender to ring.
  const visibleThreatHaloPoints = React.useMemo(() => (
    (visibleThreatCues || []).map(cue => {
      const marker = markersForSide(renderDoc, cue.target_side).find(m => m.id === cue.target_actor_id);
      if (!marker) return null;
      const { px, py } = cue.target_side === 'our'
        ? fieldToScreen(doc.field_config, marker.start.x, marker.start.y)
        : opponentFieldToScreen(doc.field_config, marker.start.x, marker.start.y);
      return { id: cue.id, px, py };
    }).filter(Boolean)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [doc, renderDoc, visibleThreatCues]);

  // Phase B.3 (§53/§19 of B.2.1) — every coach-created Moment from BOTH
  // phases, merged into ONE fullScrub-ordered list for the bottom HUD's
  // scrubber ticks + chip row. Snap/End/Formation are never included
  // here (they're protected anchors, not Moments — see
  // PlaybookTimelineControls.js's own dedicated anchor buttons).
  // lifecycleToFullScrub() is what lets a Pre-Snap Moment's phase-local
  // position and a Post-Snap Moment's phase-local position sit correctly
  // relative to each other on the SAME physical slider despite being on
  // two different numeric scales internally.
  const displayMoments = [
    ...preSnapKeyframesSorted.filter(k => k.kind === 'keyframe').map(k => ({ id: k.id, label: k.label, fullScrub: lifecycleToFullScrub('pre_snap', k.position) })),
    ...postSnapKeyframesSorted.filter(k => k.kind === 'keyframe').map(k => ({ id: k.id, label: k.label, fullScrub: lifecycleToFullScrub('post_snap', k.position) })),
  ].sort((a, b) => a.fullScrub - b.fullScrub);

  // Phase B.2.4 — field-first UX. The whole workspace (top HUD, field,
  // bottom HUD) is now ONE bordered/backgrounded container instead of
  // three separately-margined page elements (a field Box, then a
  // PlaybookTimelineControls Card, then a selected-actor Card) — this is
  // what makes it read as one coaching-app surface rather than a webpage
  // with a diagram embedded in it. The field-wrapper Box below no longer
  // caps its own width (that cap now lives once, on PlayEditorView's
  // outer Box) — it simply fills whatever width the unified container
  // gives it. wrapperRef stays on THIS Box (immediately wrapping
  // FootballField) since clientToScreen()'s pointer math is relative to
  // exactly this element's bounding rect — moving it up to the outer
  // container (which now also contains HUD padding) would silently
  // throw off every field-tap coordinate.
  // Phase B.2.4a — TRUE field-overlay HUD. The outer Box is now just a
  // rounded/bordered FRAME with zero padding of its own; the inner Box
  // (ref={wrapperRef}) is the `position: relative` positioning context,
  // wraps FootballField with ZERO padding/border of its own, and is the
  // ONLY thing clientToScreen()'s pointer math ever measures — its
  // getBoundingClientRect() now corresponds EXACTLY to the rendered SVG
  // (no padding/border inset skewing the click→field-coordinate mapping,
  // eliminating even the small theoretical error the B.2.4 padded
  // wrapper had). Both HUDs are `position: absolute` DESCENDANTS of this
  // same relative container — CSS positioning only cares about the
  // nearest positioned ancestor, not DOM sibling order, so the HUDs stay
  // exactly where they were in source order (still perfectly valid) and
  // simply no longer participate in normal document flow, meaning the
  // FIELD alone determines this container's size — it visually IS the
  // background, with HUD trays floating over it (§4/§5/§6 of the spec).
  return (
    <>
      <Box sx={{ border: '1px solid #1e2a3a', borderRadius: 2, overflow: 'hidden' }}>
        <Box ref={wrapperRef} sx={{ position: 'relative' }}>
        {/* TOP HUD — selected-actor RESPONSIBILITY + MOMENT POSITION
            (§8/§9/§22). ABSENT ENTIRELY when nothing is selected (§7 —
            no persistent instruction bar, no reserved space), and ALSO
            hidden while playback is running (B.2.4b §14 — "the play
            itself should become the visual focus"; selection is kept
            internally so this reappears automatically on pause/end
            rather than forcing a re-tap). The exact
            same state machine as before — RESPONSIBILITY still reads/
            writes post_snap.assignments, MOMENT POSITION still reads/
            writes timeline.actor_state, never merged — now floats over
            the field's top edge as a compact translucent tray instead
            of a normal-flow row. `pointerEvents: 'none'` on the full-
            width positioning wrapper + `'auto'` on just the tray itself
            (§23) means a tap anywhere in the reserved top strip that
            ISN'T on the tray still reaches the field/markers below it. */}
        {!readOnly && selectedMarker && !playing && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
            display: 'flex', justifyContent: 'center',
            p: { xs: 0.5, sm: 1 }, pointerEvents: 'none',
          }}>
            <Box sx={{
              pointerEvents: 'auto', width: { xs: 'calc(100% - 16px)', sm: '95%' }, maxWidth: 960,
              backgroundColor: 'rgba(8,16,28,0.5)', backdropFilter: 'blur(3px)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2,
              boxShadow: '0 2px 14px rgba(0,0,0,0.35)',
              px: { xs: 1, sm: 1.75 }, py: { xs: 0.625, sm: 0.875 },
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {phase === 'pre_snap' ? (
                // Phase B.3 (§26/§47) — Pre-Snap gets its OWN small,
                // deliberately minimal Top HUD branch, never the
                // Post-Snap responsibility palette (Route/Block/etc. do
                // not exist before Snap). No Moment Position sub-panel
                // this pass either (§43's own "acceptable, not
                // required" — see this file's Pre-Snap section note
                // near markThisPoint()); a Pre-Snap Moment still exists
                // purely as a scrubber bookmark, renameable/deletable
                // from the bottom HUD exactly like a Post-Snap one.
                <>
                  {!(selectedPreSnapAssignment && fieldMode === 'select') && !(selectedSetPosition && !atFormationAnchor && fieldMode === 'select') && (
                    <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem', fontWeight: 700 }}>
                      {selectedLabel} · PRE-SNAP
                    </Typography>
                  )}
                  {fieldMode === 'draw' ? (
                    <>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained" onClick={finishDrawing} disabled={drawingPath.length === 0}
                          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 3 }}
                        >
                          Finish ({drawingPath.length} pt{drawingPath.length === 1 ? '' : 's'})
                        </Button>
                        <Button onClick={cancelDrawing} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44, px: 2.5 }}>Cancel</Button>
                      </Box>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem' }}>Editing from Formation.</Typography>
                      <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700 }}>
                        MOTION — tap the field to add {selectedLabel}'s pre-snap path.
                      </Typography>
                    </>
                  ) : selectedPreSnapAssignment ? (
                    <>
                      <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700 }}>
                        {selectedLabel} · Motion
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" color="primary" onClick={startPreSnapMotion} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 2.5 }}>
                          Redraw
                        </Button>
                        <Button color="error" onClick={clearPreSnapMotion} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 2.5 }}>
                          Clear
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Phase B.3b (§10/§39) — static Pre-Snap set-
                          position summary/reset, shown only away from
                          Formation (dragging AT Formation edits
                          Formation itself — see handlePointerDown's own
                          note — so this messaging would be misleading
                          there). Merges into one compact bold line
                          exactly like the Post-Snap "resting assignment"
                          pattern (B.2.4b), rather than a separate
                          always-on label + separate summary line. */}
                      {selectedSetPosition && !atFormationAnchor && (
                        <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700 }}>
                          {selectedLabel} · Set for Snap
                        </Typography>
                      )}
                      {!selectedSetPosition && !atFormationAnchor && (
                        <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>
                          Drag {selectedLabel} to set their position at Snap.
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedSetPosition && !atFormationAnchor && (
                          <Button
                            onClick={() => clearPreSnapSetPosition(selected.side, selected.id)}
                            sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44, px: 2 }}
                          >
                            Use Formation Position
                          </Button>
                        )}
                        <Button
                          variant="contained" onClick={startPreSnapMotion}
                          sx={{ textTransform: 'none', fontWeight: 800, minHeight: 48, px: 2.5, backgroundColor: '#16324f', '&:hover': { backgroundColor: '#1e4066' } }}
                        >
                          + Add Motion
                        </Button>
                      </Box>
                    </>
                  )}
                </>
              ) : (
              <>
              {/* B.2.4b (§8/§9): the muted label header collapses away
                  entirely in the resting-assignment state, where it gets
                  folded into the bolder responsibility line instead (see
                  the `selectedAssignment && !changingResponsibility`
                  branch below). Phase B.3h.3: ALSO collapses away for the
                  no-assignment PALETTE state now (`showingPalette`) — that
                  branch folds this same label into its own merged Row 1
                  instead (§Row 1 condensation: "related controls grouped
                  horizontally"). Only mid-draw/mid-Zone/pendingType-choice
                  still show it as a plain standalone line, unchanged.
                  Phase B.3c (§4): "OUR"/"OPP" dropped from this header
                  (and from the resting line below) — the selected
                  marker's own color/selection ring already communicates
                  side; repeating it in text was redundant chrome the
                  compact-toolbar goal doesn't need. */}
              {!restingWithAssignment && !showingPalette && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem', fontWeight: 700 }}>
                    {selectedLabel}
                  </Typography>
                  {passingReadStatusChip}
                  {threatStatusChip}
                </Box>
              )}

              {fieldMode === 'draw' ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained" onClick={finishDrawing} disabled={drawingPath.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 3 }}
                    >
                      Finish ({drawingPath.length} pt{drawingPath.length === 1 ? '' : 's'})
                    </Button>
                    <Button onClick={cancelDrawing} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44, px: 2.5 }}>Cancel</Button>
                  </Box>
                  {/* §12 of the B.2.3 spec — makes the automatic scrub-to-
                      Snap transition (beginFieldAuthoring(), see
                      startDrawing() above) obvious rather than feeling
                      like the field randomly jumped. */}
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem' }}>Editing from Snap.</Typography>
                  {fieldInstruction && (
                    <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700 }}>{fieldInstruction}</Typography>
                  )}
                </>
              ) : fieldMode === 'threat_select' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: '#e0483a', fontSize: '0.85rem', fontWeight: 700 }}>
                    Select defender for {selectedLabel}&apos;s Threat
                  </Typography>
                  <Button onClick={cancelThreatSelect} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44, px: 2.5 }}>Cancel</Button>
                </Box>
              ) : fieldMode === 'zone' ? (
                <>
                  <Button onClick={cancelZonePlacement} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44, px: 2.5, alignSelf: 'flex-start' }}>Cancel</Button>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem' }}>Editing from Snap.</Typography>
                  {fieldInstruction && (
                    <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700 }}>{fieldInstruction}</Typography>
                  )}
                </>
              ) : pendingType === 'route' ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ color: '#9eb8d0', fontSize: '0.8rem', mr: 0.5 }}>Progression:</Typography>
                  {[1, 2, 3, null].map(v => (
                    <Button
                      key={String(v)} variant="outlined"
                      onClick={() => { setProgressionChoice(v); startDrawing(); }}
                      sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 2.5, borderColor: routeColorFor(v), color: routeColorFor(v) }}
                    >
                      {progressionLabel(v)}
                    </Button>
                  ))}
                  <Button onClick={() => setPendingType(null)} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 44 }}>Cancel</Button>
                </Box>
              ) : pendingType === 'man' ? (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="man-target-label">Target</InputLabel>
                    <Select
                      labelId="man-target-label" label="Target" value={targetKey}
                      onChange={e => { const value = e.target.value; setTargetKey(value); commitMan(value); }}
                    >
                      <MenuItem value="">No target</MenuItem>
                      {targetSideMarkers.map(m => (
                        <MenuItem key={m.id} value={m.id}>{displayLabel(targetDisplayLabels, m)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button onClick={() => setPendingType(null)} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 40 }}>Cancel</Button>
                </Box>
              ) : pendingType && RECIPIENT_TYPES.has(pendingType) ? (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="recipient-label">{pendingType === 'handoff' ? 'Recipient' : 'Intended Receiver'}</InputLabel>
                    <Select
                      labelId="recipient-label" label={pendingType === 'handoff' ? 'Recipient' : 'Intended Receiver'} value={targetKey}
                      onChange={e => {
                        const value = e.target.value;
                        setTargetKey(value);
                        if (selectedAssignment) changeRecipient(value);
                        else if (value) startDrawing();
                      }}
                    >
                      {recipientSideMarkers.map(m => (
                        <MenuItem key={m.id} value={m.id}>{displayLabel(selectedDisplayLabels, m)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button onClick={() => setPendingType(null)} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 40 }}>Cancel</Button>
                </Box>
              ) : selectedAssignment && !changingResponsibility ? (
                // Phase B.3h.3 (§Row 1 condensation, Control Group 1) —
                // ONE combined row: the responsibility state/actions
                // cluster (left) and the Passing Read status/actions
                // cluster (right), where before this pass they were two
                // separately-stacked rows (a header-line-plus-chip row,
                // then a full buttons-plus-editControls row). The
                // responsibility SUMMARY itself ({selectedLabel} ·
                // {existingSummary}) stays plain bold Typography — not a
                // button — so it visually reads as CURRENT STATE, while
                // every actual control beside it remains a real Button —
                // ACTIONS. flexWrap lets this gracefully break onto a
                // second visual line on narrow widths without ever
                // becoming a THIRD reserved row (§Responsive: "prefer
                // wrapping within the two-row structure").
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ color: '#e8f0f8', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                      {selectedLabel} · {existingSummary}
                    </Typography>
                    {normalizeAssignmentType(selectedAssignment) === 'route' && (
                      [1, 2, 3, null].map(v => (
                        <Button
                          key={String(v)} size="small" variant={selectedAssignment.progression === v ? 'contained' : 'outlined'}
                          onClick={() => setRouteProgressionInPlace(v)}
                          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 36, borderColor: routeColorFor(v), color: selectedAssignment.progression === v ? '#0a1420' : routeColorFor(v), backgroundColor: selectedAssignment.progression === v ? routeColorFor(v) : undefined }}
                        >
                          {progressionLabel(v)}
                        </Button>
                      ))
                    )}
                    {/* Phase B.3h.4 (Task 2/3) — Target/Redraw/Change all
                        share ONE compact "secondary action" treatment
                        (plain text button, muted color, no border/fill)
                        so the responsibility SUMMARY just above them
                        stays the strongest visual anchor on this row —
                        none of these compete with it for weight anymore.
                        Renamed "Change Target" and "Recipient" → "Target"
                        (same handlers, unchanged behavior — inspected
                        first, per Task 2: Target picks WHO this
                        responsibility targets without touching its path;
                        Redraw re-authors the PATH shape, keeping the same
                        type/target; Change reveals the primary palette to
                        pick a DIFFERENT responsibility type entirely —
                        three genuinely distinct actions, kept, just
                        consistently labeled/styled now). minHeight kept
                        at 40 (comfortable tablet tap target — §Task 3:
                        "less button chrome, not tiny buttons"). */}
                    {normalizeAssignmentType(selectedAssignment) === 'man' && (
                      <Button
                        onClick={() => { setPendingType('man'); setTargetKey(selectedAssignment.target_actor_id || ''); }}
                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5, color: '#9eb8d0' }}
                      >
                        Target
                      </Button>
                    )}
                    {RECIPIENT_TYPES.has(normalizeAssignmentType(selectedAssignment)) && (
                      <Button
                        onClick={() => { setPendingType(normalizeAssignmentType(selectedAssignment)); setTargetKey(selectedAssignment.target_actor_id || ''); }}
                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5, color: '#9eb8d0' }}
                      >
                        Target
                      </Button>
                    )}
                    {normalizeAssignmentType(selectedAssignment) !== 'zone' && normalizeAssignmentType(selectedAssignment) !== 'man' && (
                      <Button
                        onClick={() => {
                          setAssignmentType(normalizeAssignmentType(selectedAssignment));
                          if (normalizeAssignmentType(selectedAssignment) === 'route') setProgressionChoice(selectedAssignment.progression ?? null);
                          startDrawing();
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5, color: '#9eb8d0' }}
                      >
                        Redraw
                      </Button>
                    )}
                    {normalizeAssignmentType(selectedAssignment) === 'zone' && (
                      <Button
                        onClick={() => { setAssignmentType('zone'); startZonePlacement(); }}
                        sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5, color: '#9eb8d0' }}
                      >
                        Redraw Zone
                      </Button>
                    )}
                    <Button onClick={() => setChangingResponsibility(true)} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5, color: '#9eb8d0' }}>
                      Change
                    </Button>
                    {/* Clear stays visually distinct (error color) —
                        it's the one destructive action in this group, so
                        it should still stand apart, just no longer
                        heavier in CHROME than its neighbors. */}
                    <Button color="error" onClick={clearAssignment} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, px: 1.5 }}>
                      Clear
                    </Button>
                    {/* Phase B.3c (§16) — compact contextual +Moment,
                        replacing the old prose ("Mark this moment to
                        adjust...") + full-width button pair that used to
                        live in the separate MOMENT POSITION panel below.
                        Only offered when NOT already sitting on a marked
                        point (Moment/Snap/End) — nothing to "add" there. */}
                    {!atMarkedPoint && (
                      <Button onClick={markThisPoint} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 1.5 }}>
                        + Moment
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {passingReadStatusChip}
                    {passingReadEditControls}
                    {threatStatusChip}
                    {threatEditControls}
                  </Box>
                </Box>
              ) : (
                // Phase B.3h.3 — the no-assignment (or Change-tapped)
                // PALETTE state (`showingPalette`) gets the SAME Row 1
                // treatment: the plain `{selectedLabel}` header (normally
                // its own standalone row — see that block's own updated
                // note above) folds into this row's LEFT cluster instead,
                // and Passing Read status/actions occupy the RIGHT
                // cluster, exactly mirroring the resting-with-assignment
                // branch just above.
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                    {selectedLabel}
                  </Typography>
                  {selectedAssignment && changingResponsibility && (
                    <Button onClick={() => setChangingResponsibility(false)} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 48 }}>
                      Keep Current
                    </Button>
                  )}
                  {primaryActions.map(p => (
                    <Button
                      key={p.type} variant="contained" onClick={() => chooseAssignmentType(p.type)}
                      sx={{ textTransform: 'none', fontWeight: 800, minHeight: 48, px: 2.5, backgroundColor: '#16324f', '&:hover': { backgroundColor: '#1e4066' } }}
                    >
                      {p.label}
                    </Button>
                  ))}
                  {(otherTypes.length > 0) && (
                    <>
                      <Button
                        variant="outlined" onClick={e => setOtherAnchor(e.currentTarget)}
                        sx={{ textTransform: 'none', fontWeight: 800, minHeight: 48, px: 2.5, color: '#9eb8d0', borderColor: '#2a3a4e' }}
                      >
                        Other
                      </Button>
                      <Menu anchorEl={otherAnchor} open={Boolean(otherAnchor)} onClose={() => setOtherAnchor(null)}>
                        {otherTypes.map(t => (
                          <MenuItem key={t} onClick={() => { setOtherAnchor(null); chooseAssignmentType(t); }}>
                            {ASSIGNMENT_TYPE_META[t].label}
                          </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem onClick={() => { setOtherAnchor(null); setEditPlayerDraft(selectedMarker.role_label); setEditPlayerOpen(true); }}>
                          Edit Player
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                  {otherTypes.length === 0 && (
                    <Button
                      variant="outlined" onClick={() => { setEditPlayerDraft(selectedMarker.role_label); setEditPlayerOpen(true); }}
                      sx={{ textTransform: 'none', fontWeight: 800, minHeight: 48, px: 2.5, color: '#9eb8d0', borderColor: '#2a3a4e' }}
                    >
                      Edit Player
                    </Button>
                  )}
                  {!atMarkedPoint && (
                    <Button onClick={markThisPoint} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44, px: 1.5, color: '#9eb8d0' }}>
                      + Moment
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {passingReadStatusChip}
                  {passingReadEditControls}
                  {threatStatusChip}
                  {threatEditControls}
                </Box>
                </Box>
              )}

              {/* Phase B.3h.3 — ROW 2: Moment/PATH (left cluster) +
                  Passing Read add/change (right cluster), merged into
                  ONE always-reserved row (Control Group 3 + Control
                  Group 4). Before this pass these were two entirely
                  separate conditionally-mounted blocks, stacked
                  vertically — up to 2 extra rows on top of Row 1. Now
                  ALWAYS rendered (with `minHeight: 40` reserved) whenever
                  the responsibility section is idle
                  (fieldMode==='select' && !pendingType — matching both
                  sub-blocks' own original individual gating), even if
                  BOTH clusters happen to be empty for the current
                  selection — so entering/leaving a Moment, gaining/
                  losing a Passing Read, or switching between "Adjust"
                  and resting PATH state never changes the Top HUD's
                  outer height (§Fixed Top HUD height: "arriving at/
                  leaving a Moment," "changing read target," "entering/
                  leaving normal read state" must not move the field).
                  Same "reserve geometry, vary only content" principle
                  B.3h.2 already established for the Bottom HUD's own
                  contextual row. */}
              {/* Phase B.3h.4 (Task 5) — the two clusters now sit
                  left-to-right with normal flow spacing (`justifyContent:
                  'flex-start'`, a slightly wider gap between them)
                  instead of `space-between` pushing the Read cluster all
                  the way to the far right — on any width wider than the
                  content itself, `space-between` left a large empty gap
                  in the middle that made this read as two unrelated
                  islands rather than one coherent Moment-related row. */}
              {fieldMode === 'select' && !pendingType && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 3, flexWrap: 'wrap', minHeight: 40 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Control Group 3 — Moment PATH. Phase B.3c
                        (§7/§8/§10/§14/§18), replacing the old "MOMENT
                        POSITION" panel. Phase B.3h (§17/§18/§19): rests
                        COLLAPSED (`M2 · PATH 51%` text + small "Adjust"
                        button, no Slider) and only reveals the Slider
                        while `adjustingMomentPath` is true — tapping
                        "Adjust" (or the text itself) reveals it; "Done"/
                        Reset collapses it back (§Control Group 3: "do not
                        expand PATH controls unless Adjust mode is
                        actually entered" — unchanged this pass). Shown
                        only AT a real coach-created Moment OR End (Snap
                        is excluded, nothing has ever been editable
                        there). Two cases: hasPathForMoment && no
                        override (the kept feature), or a legacy
                        `override` (no slider possible — compact label +
                        Reset is the only remaining way to touch it). */}
                    {!isSnapKeyframe && atMarkedPoint && (momentState?.override || hasPathForMoment) && (
                      momentState?.override ? (
                        <>
                          <Typography sx={{ color: '#9eb8d0', fontSize: '0.75rem', flexShrink: 0 }}>
                            {selectedKeyframe.label} · Custom position (legacy)
                          </Typography>
                          <Button
                            size="small" onClick={() => onChangeTimeline(clearActorKeyframeState(timeline, selected.side, selected.id, selectedKeyframe.id))}
                            sx={{ textTransform: 'none', minHeight: 36, color: '#6a8aa8' }}
                          >
                            Reset
                          </Button>
                        </>
                      ) : adjustingMomentPath ? (
                        <>
                          <Typography sx={{ color: '#9eb8d0', fontSize: '0.75rem', flexShrink: 0 }}>
                            {selectedKeyframe.label} · PATH {momentProgressPct}%
                          </Typography>
                          <Slider
                            value={momentProgressPct} min={0} max={100} step={1}
                            onChange={(e, v) => onChangeTimeline(setActorKeyframeProgress(timeline, selected.side, selected.id, selectedKeyframe.id, v / 100))}
                            sx={{ flex: 1, minWidth: 100, maxWidth: 220 }}
                          />
                          {momentState && (
                            <Button
                              size="small" onClick={() => onChangeTimeline(clearActorKeyframeState(timeline, selected.side, selected.id, selectedKeyframe.id))}
                              sx={{ textTransform: 'none', minHeight: 36, color: '#6a8aa8' }}
                            >
                              Reset
                            </Button>
                          )}
                          <Button size="small" onClick={() => setAdjustingMomentPath(false)} sx={{ textTransform: 'none', minHeight: 36, color: '#6a8aa8' }}>
                            Done
                          </Button>
                        </>
                      ) : (
                        // RESTING: text only, no slider (§20 — the text
                        // itself is ALSO tappable, a slightly larger
                        // effective target than the Adjust button alone).
                        <>
                          <Typography
                            onClick={() => setAdjustingMomentPath(true)}
                            sx={{ color: '#9eb8d0', fontSize: '0.75rem', flexShrink: 0, cursor: 'pointer' }}
                          >
                            {selectedKeyframe.label} · PATH {momentProgressPct}%
                          </Typography>
                          <Button size="small" onClick={() => setAdjustingMomentPath(true)} sx={{ textTransform: 'none', minHeight: 36 }}>
                            Adjust
                          </Button>
                        </>
                      )
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Control Group 4 — available/eligible Passing Read
                        targets, and the "Change" scoped-target picker.
                        Phase B.3h (§11-§16/§29/§38): QB-only, Post-Snap-
                        only, Moment-owned. No Moment selected → nothing
                        renders here (the responsibility toolbar's OWN
                        "+ Moment" — Row 1 — is the one contextual +Moment
                        action; no redundant duplicate). Existing reads'
                        status/Edit/Remove live in Row 1
                        (passingReadStatusChip/passingReadEditControls) —
                        this cluster is only ever the temporary "Change"
                        target picker OR the one-tap "add" buttons for any
                        progression not yet covered (§15) — both stay
                        within this SAME reserved row (§Control Group 4:
                        "do not create another full-height row"). */}
                    {isSelectedOurQB && selectedMoment && (
                      changingReadProgression !== null ? (() => {
                        const targets = eligibleReadTargets(changingReadProgression);
                        if (targets.length === 0) {
                          return (
                            <>
                              <Typography sx={{ color: '#e0a73a', fontSize: '0.75rem' }}>
                                No Read {changingReadProgression} route is assigned.
                              </Typography>
                              <Button size="small" onClick={cancelChangeRead} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 36 }}>Cancel</Button>
                            </>
                          );
                        }
                        return (
                          <>
                            <Typography sx={{ color: '#9eb8d0', fontSize: '0.75rem', flexShrink: 0 }}>Read {changingReadProgression}:</Typography>
                            {targets.map(t => (
                              <Button
                                key={t.id} size="small" variant="outlined"
                                onClick={() => commitPassingRead(selectedMoment, changingReadProgression, 'our', t.id)}
                                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, px: 1.5 }}
                              >
                                {displayLabel(ourDisplayLabels, t)}
                              </Button>
                            ))}
                            <Button size="small" onClick={cancelChangeRead} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 36 }}>Cancel</Button>
                          </>
                        );
                      })() : (() => {
                        const usedProgressions = new Set(qbMomentPassingReads.map(c => c.progression));
                        const addable = eligibleReadRoutes.filter(a => !usedProgressions.has(a.progression));
                        if (addable.length === 0) {
                          return qbMomentPassingReads.length === 0
                            ? <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>No Read routes assigned yet.</Typography>
                            : null;
                        }
                        return (
                          <>
                            {/* Phase B.3h.4 (Task 5) — always shown
                                whenever there's at least one addable Read
                                button (previously only shown when NO read
                                existed yet at all), so this cluster reads
                                as "Reads: [+ WR2 · 2] [+ WR3 · 3]" — a
                                small persistent label rather than
                                appearing/disappearing depending on
                                whether other reads already exist. */}
                            <Typography sx={{ color: '#9eb8d0', fontSize: '0.75rem', flexShrink: 0 }}>Reads:</Typography>
                            {addable.map(a => {
                              const marker = markersForSide(doc, 'our').find(m => m.id === a.actor_id);
                              if (!marker) return null;
                              return (
                                <Button
                                  key={a.id} size="small" variant="outlined"
                                  onClick={() => commitPassingRead(selectedMoment, a.progression, 'our', a.actor_id)}
                                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, px: 1.5 }}
                                >
                                  + {displayLabel(ourDisplayLabels, marker)} · {a.progression}
                                </Button>
                              );
                            })}
                          </>
                        );
                      })()
                    )}
                  </Box>
                </Box>
              )}
              </>
              )}
              </Box>
            </Box>
          </Box>
        )}

        <FootballField fieldConfig={doc.field_config} onFieldPointerDown={handleFieldPointerDown}>
          {/* Phase B.3a (§10-§25) — PHASE-AWARE ART VISIBILITY. Before
              Snap, the coach should see only "how do we get to Snap"
              (Pre-Snap Motion); at/after Snap, only "what happens once
              the ball is snapped" (every Post-Snap responsibility type —
              Zone/Man/Route/Block/Rush/etc.), never both complete art
              systems layered on top of each other. Gated purely on
              `phase` (derived from the CURRENT fullScrub) — no special-
              casing needed for the "still show what's being actively
              edited" authoring exception (§16-§18), because EVERY
              authoring entry point already forces fullScrub to its own
              phase's anchor first (beginFieldAuthoring() — Formation for
              Pre-Snap Motion, Snap for Post-Snap responsibilities), so
              `phase` is already correct for whichever pool is being
              edited by the time any of this renders. Visibility only —
              no assignment data, path, or position-resolution logic
              changed by this gate (§29). Zones render first (backmost)
              regardless of creation order, so a route/block/man drawn
              later never gets buried under a translucent area — see
              buildDisplayLabelMap()'s sibling z-order note above. */}
          {phase === 'post_snap' && postSnapZoneElements.map(z => {
            // Only the arrow's START point legitimately depends on the
            // resolved (renderDoc) defender position — the circle itself
            // (postSnapZoneElements) is already memoized above (§39-§46).
            const a = assignments.find(x => x.id === z.id);
            const arrow = a ? zoneArrowScreenPoints(renderDoc, a) : null; // arrow START follows the resolved defender in PTP mode
            return (
              <React.Fragment key={z.id}>
                <ZoneArea center={z.center} radiusPx={z.radiusPx} />
                {arrow && <ZoneArrow start={arrow.start} end={arrow.end} />}
              </React.Fragment>
            );
          })}
          {/* Pre-Snap Motion artwork — visible ONLY before Snap now
              (§24; was "always visible" in plain B.3, deliberately
              changed this pass). Memoized above (§39-§46) — see
              preSnapMotionElements' own note. */}
          {phase === 'pre_snap' && preSnapMotionElements.map(el => (
            <AssignmentPath key={el.id} points={el.points} type="motion" />
          ))}
          {phase === 'post_snap' && assignments.map(a => {
            // Man is the one responsibility type that legitimately
            // depends on renderDoc every tick (its connection tracks
            // BOTH actors' live resolved positions) — kept inline,
            // unmemoized, on purpose. Everything else routes through
            // postSnapPathElements below instead.
            if (normalizeAssignmentType(a) !== 'man') return null;
            const geo = manConnectionScreenPoints(renderDoc, a); // follows resolved positions in PTP mode
            return geo ? <ManConnection key={a.id} start={geo.start} end={geo.end} /> : null;
          })}
          {/* Memoized above (§39-§46) — see postSnapPathElements' own
              note. Phase B.3 (§33): each element's IMPLICIT first point
              is the actor's resolved SNAP position (Formation if no
              Pre-Snap motion — resolveSnapPosition()'s own fallback,
              byte-identical to the pre-B.3 behavior) — every
              subsequently authored waypoint stays exactly where the
              coach drew it. */}
          {phase === 'post_snap' && postSnapPathElements.map(el => (
            <AssignmentPath key={el.id} points={el.points} type={el.type} progression={el.progression} />
          ))}
          {drawingPreviewPoints && (
            <polyline
              points={drawingPreviewPoints.map(p => `${p.px},${p.py}`).join(' ')}
              fill="none" stroke="#fff" strokeWidth={1.5} strokeDasharray="2 2"
            />
          )}
          {/* Phase B.3e — Passing Read teaching cue. Rendered AFTER all
              committed assignment artwork but BEFORE the marker circles
              (§19/§20 of B.3e — "readable without obscuring player
              markers"), so the dashed line/numbered node sit visually
              UNDER the actual player tokens rather than covering them.
              Phase B.3h (§28) re-evaluated this exact layering choice
              for the new, much smaller numbered ActionNode: since the
              node is deliberately tiny (ACTION_NODE_RADIUS=5.5, smaller
              than a player marker) and no longer competes for attention
              the way B.3e/B.3f's large rectangular "READ N" text badge
              did, letting a player marker win a direct overlap (this
              ordering) reads correctly — the coach can still always see
              a hint of the node's dashed line and blue fill peeking out,
              and the player itself (the far more important thing to see
              clearly at a glance) is never obscured. Kept UNCHANGED from
              B.3e's own ordering rather than moved above markers. */}
          {visiblePassingReadCue && visiblePassingReadCuePoints && (
            <PassingReadCue
              start={visiblePassingReadCuePoints.start} end={visiblePassingReadCuePoints.end}
              progression={visiblePassingReadCue.progression}
            />
          )}
          {/* Phase B.4a — Threat halo(s). Same "let players win the
              overlap" z-order rule as PassingReadCue immediately above:
              drawn before the marker-circle loops so the defender's own
              token always renders on top of the ring, never obscured by
              it. Visually distinct from Passing Read on purpose (warm-red
              ring vs. blue dashed line/node) per the phase spec. */}
          {visibleThreatHaloPoints.map(pt => (
            <ThreatHalo key={pt.id} x={pt.px} y={pt.py} />
          ))}
          {renderDoc.opponent.markers.map(m => {
            const { px, py } = opponentFieldToScreen(doc.field_config, m.start.x, m.start.y);
            return (
              <PlaybookMarker
                key={`opp-${m.id}`} px={px} py={py} label={m.role_label} side="opponent"
                selected={selected?.side === 'opponent' && selected.id === m.id}
                onPointerDown={readOnly ? undefined : handlePointerDown('opponent', m.id)}
                onPointerMove={readOnly ? undefined : handlePointerMove}
                onPointerUp={readOnly ? undefined : handlePointerUp}
              />
            );
          })}
          {renderDoc.our.markers.map(m => {
            const { px, py } = fieldToScreen(doc.field_config, m.start.x, m.start.y);
            return (
              <PlaybookMarker
                key={`our-${m.id}`} px={px} py={py} label={m.role_label} side="ours"
                selected={selected?.side === 'our' && selected.id === m.id}
                onPointerDown={readOnly ? undefined : handlePointerDown('our', m.id)}
                onPointerMove={readOnly ? undefined : handlePointerMove}
                onPointerUp={readOnly ? undefined : handlePointerUp}
              />
            );
          })}
        </FootballField>

        {/* BOTTOM HUD — timeline/playback (§14/§15). ALWAYS present (no
            more [Design][PTP] toggle to gate it — "the timeline should
            simply no longer require entering PTP mode") — unlike the top
            HUD, this doesn't disappear, since Play/scrub/Mark-Moment
            should always be reachable. Same absolute-overlay + pointer-
            events pattern as the top HUD (§23): the full-width
            positioning wrapper is inert, only the translucent tray
            itself is tappable, so field taps beside it still land on the
            field. `disabled` (true while actively drawing/placing a
            Zone) keeps the whole control surface visible-but-inert
            rather than hidden, so it can never conflict with an in-
            progress field gesture (§20). B.2.4b (§14): Play no longer
            clears the current selection — it stays selected internally
            so its HUD can reappear on pause/end; only the TOP HUD's own
            render guard (`!playing`) hides it while playback runs.
            PlaybookTimelineControls itself no longer draws its own Card
            (see that file's own header note) — it only renders its own
            content now, letting THIS tray own the translucent
            background. */}
        {!readOnly && (
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
            display: 'flex', justifyContent: 'center',
            p: { xs: 0.5, sm: 1 }, pointerEvents: 'none',
          }}>
            <Box sx={{
              pointerEvents: 'auto', width: { xs: 'calc(100% - 16px)', sm: '96%' }, maxWidth: 1000,
              backgroundColor: 'rgba(8,16,28,0.5)', backdropFilter: 'blur(3px)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2,
              boxShadow: '0 2px 14px rgba(0,0,0,0.35)',
              px: { xs: 0.75, sm: 1.5 }, py: { xs: 0.625, sm: 0.875 },
            }}>
          <PlaybookTimelineControls
            fullScrub={fullScrub}
            moments={displayMoments}
            selectedKeyframe={selectedKeyframe}
            renamingKeyframeId={renamingKeyframeId}
            renameDraft={renameDraft}
            playing={playing}
            // B.2.4b (§14) — no longer clears selection before playing.
            // The Top HUD hiding during playback (see the `!playing`
            // guard on that overlay's render condition above) already
            // achieves "field becomes the focus while playing" on its
            // own; keeping `selected` intact means the still-selected
            // actor's HUD simply reappears the instant playback pauses/
            // ends, instead of forcing the coach to re-tap the player —
            // the spec's own preferred "preserve selection internally"
            // option. Phase B.3: usePlaybackAnimation's own min/max are
            // now FORMATION_FULL_SCRUB/END_FULL_SCRUB (see the hook
            // instantiation above) — "Play at End restarts from
            // Formation" (§24) falls out of the hook's existing,
            // completely unmodified "restart from min" behavior for
            // free.
            onPlay={play}
            onPause={pause}
            disabled={fieldMode !== 'select'}
            onScrub={setFullScrub}
            onMarkThisPoint={markThisPoint}
            onStartRename={(id, label) => { setRenamingKeyframeId(id); setRenameDraft(label); }}
            onRenameDraftChange={setRenameDraft}
            onRenameCommit={() => { onChangeTimeline(renameKeyframe(timeline, renamingKeyframeId, renameDraft)); setRenamingKeyframeId(null); }}
            onRenameCancel={() => setRenamingKeyframeId(null)}
            onDelete={(id) => onChangeTimeline(deleteKeyframe(timeline, id))}
              />
            </Box>
          </Box>
        )}
        </Box>
      </Box>

    {/* Role Label editing (§4) — relocated out of the normal
          assignment workflow into a small secondary dialog reached from
          Other → Edit Player. Storage (role_label) is unchanged; this is
          purely a UI relocation. */}
      <Dialog open={editPlayerOpen} onClose={() => setEditPlayerOpen(false)}>
        <DialogTitle>Edit Player</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Role label" value={editPlayerDraft}
            onChange={e => setEditPlayerDraft(e.target.value)}
            placeholder="QB, X, Mike, CB1, Sniffer, Monster, Rover…"
            sx={{ mt: 1, minWidth: 280 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPlayerOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const patch = { role_label: editPlayerDraft };
              if (selected.side === 'our') updateOurMarker(selected.id, patch);
              else updateOpponentMarker(selected.id, patch);
              setEditPlayerOpen(false);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** One committed PATH-drawn assignment — a plain polyline styled almost
 * entirely from the semantic `type` (ASSIGNMENT_TYPE_META above, already
 * normalized by the caller): dashed vs. solid and endpoint treatment.
 * Color is normally `meta.color` EXCEPT for Route, whose color is
 * instead derived from its own persisted `progression` (routeColorFor())
 * — Route is the one type whose META color entry is deliberately null
 * (see ASSIGNMENT_TYPE_META's own note). The stored data never encodes
 * color/shape directly, only `type` (+`progression` for Route) — this
 * component is the one place that decides what each type LOOKS like.
 * Four endpoint symbols exist (§34's ARROW/T/X/O visual language):
 *   arrow — player movement direction (Route/Movement/Run/Pass Rush/
 *           Blitz/Run Fit)
 *   bar   — intended blocking/contact point (Block/Run Block/Pass Block)
 *           — positioned by assignmentScreenPoints()/blockContactPoint()
 *           to stop at a legacy target's marker boundary when present.
 *   x     — ball exchange / handoff point (Handoff)
 *   o     — football release/pass point (Pass) */
// Phase B.3h (§8) — React.memo on every small presentational SVG piece
// rendered per-assignment/per-cue (AssignmentPath, ManConnection,
// PassingReadCue, ZoneArea, ZoneArrow below): a low-risk, purely-additive
// complement to renderDoc's own B.3h throttle (see that constant's own
// header note) — these are pure functions of their own props with no
// internal state, so a shallow-equal prop comparison is always safe, and
// it lets React skip re-invoking/reconciling any of them on a tick where
// their own specific inputs happen not to have changed (e.g. a Man
// connection between two actors who both currently sit still).
const AssignmentPath = React.memo(function AssignmentPath({ points, type, progression }) {
  const meta = ASSIGNMENT_TYPE_META[type] || ASSIGNMENT_TYPE_META.movement;
  const color = type === 'route' ? routeColorFor(progression) : meta.color;
  const pointsAttr = points.map(p => `${p.px},${p.py}`).join(' ');
  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const angleDeg = (Math.atan2(last.py - prev.py, last.px - prev.px) * 180) / Math.PI;
  return (
    <g>
      <polyline
        points={pointsAttr} fill="none" stroke={color} strokeWidth={1.5}
        strokeDasharray={meta.dashed ? '4 3' : undefined}
      />
      {meta.endpoint === 'bar' && (
        <line
          x1={-6} y1={0} x2={6} y2={0} stroke={color} strokeWidth={2.5}
          transform={`translate(${last.px},${last.py}) rotate(${angleDeg + 90})`}
        />
      )}
      {meta.endpoint === 'arrow' && (
        <polygon points="-4,-3 4,0 -4,3" fill={color} transform={`translate(${last.px},${last.py}) rotate(${angleDeg})`} />
      )}
      {meta.endpoint === 'x' && (
        <g transform={`translate(${last.px},${last.py})`}>
          <line x1={-4} y1={-4} x2={4} y2={4} stroke={color} strokeWidth={2} />
          <line x1={-4} y1={4} x2={4} y2={-4} stroke={color} strokeWidth={2} />
        </g>
      )}
      {meta.endpoint === 'o' && (
        <circle cx={last.px} cy={last.py} r={4} fill="none" stroke={color} strokeWidth={2} />
      )}
    </g>
  );
});

/** A Man responsibility connection — a thin, subtle white dashed line
 * between the defender and their assigned offensive actor (computed
 * fresh every render from both actors' live positions by
 * manConnectionScreenPoints(), never drawn/stored as path waypoints —
 * see this file's own "Man is not a movement path" note). Small dot
 * endpoint cues instead of a route-style arrowhead — this is a
 * responsibility reference, not a direction of travel. */
const ManConnection = React.memo(function ManConnection({ start, end }) {
  const meta = ASSIGNMENT_TYPE_META.man;
  return (
    <g>
      <line
        x1={start.px} y1={start.py} x2={end.px} y2={end.py}
        stroke={meta.color} strokeWidth={1} strokeDasharray="2 2" opacity={0.85}
      />
      <circle cx={start.px} cy={start.py} r={2} fill={meta.color} />
      <circle cx={end.px} cy={end.py} r={2} fill={meta.color} />
    </g>
  );
});

// Phase B.3h (§21-§29) — a small, GENERIC numbered action node. This is
// the field's first visual hint of a future player action-node system
// (§27 — "QB path: ───●1────●2────●RUN──>", "OL path:
// ───●BLOCK────●CLIMB──>") — deliberately NOT hardcoded to Passing Read
// alone even though PassingReadCue below is its only caller today: a
// future Block/Climb/Run action node could reuse this exact component
// by passing a different `label`/`color`, without this component ever
// needing to know what kind of action it's marking. Intentionally
// DISPLAY-ONLY for this pass (§26 — no drag, no tap editor, no
// long-press menu; that belongs to future Path Action work). Radius is
// deliberately SMALLER than a player marker (PlaybookMarker's own
// VISUAL_RADIUS=6.75) and visually distinct from both a player token
// and a Snap/Moment timeline landmark (a filled circle + light border +
// bold light number, no rectangular text backing — §21/§29: "no large
// READ text badge on the field, the node number is enough, the full
// label stays in the Top HUD").
const ACTION_NODE_RADIUS = 5.5;
const ActionNode = React.memo(function ActionNode({ x, y, label, color }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle r={ACTION_NODE_RADIUS} fill={color} fillOpacity={0.92} stroke="#e8f0f8" strokeWidth={1} />
      <text textAnchor="middle" dominantBaseline="central" fontSize={6.5} fontWeight={800} fill="#f4f8fc" style={{ userSelect: 'none' }}>
        {label}
      </text>
    </g>
  );
});

/** Phase B.3e, endpoints re-targeted in B.3f, field visual replaced in
 * B.3h — the Passing Read teaching cue: "THIS is WHERE Read N happens on
 * the route" — explicitly NOT a throw (see this file's own header note
 * on the feature). Reuses the SAME `pass` blue (§18 of B.3e — "use the
 * existing blue language associated with QB Pass") but stays visually
 * distinct from an actual authored Pass responsibility (AssignmentPath's
 * own solid line + 'o' release-point endpoint): here always DASHED,
 * terminating at a small numbered ActionNode instead of B.3f/B.3e's
 * large rectangular "READ N" text badge — browser/iPad testing showed
 * that badge visually competed with/got dominated by player markers and
 * play art (§21). The node's own number conveys which Read this is; the
 * FULL "READ N → target" text now lives exclusively in the Top HUD
 * (§29). The line is pulled back by ACTION_NODE_RADIUS so it terminates
 * at, not through, the node (§25). `start`/`end` are purely
 * presentational screen points — this component has no idea `end` is a
 * route landmark rather than a receiver marker (see
 * passingReadCueScreenPoints()'s own header note); it just draws a line
 * and a node between two points. */
const PassingReadCue = React.memo(function PassingReadCue({ start, end, progression }) {
  const color = ASSIGNMENT_TYPE_META.pass.color;
  const dx = end.px - start.px;
  const dy = end.py - start.py;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  const lineEnd = { px: end.px - ux * ACTION_NODE_RADIUS, py: end.py - uy * ACTION_NODE_RADIUS };
  return (
    <g>
      <line
        x1={start.px} y1={start.py} x2={lineEnd.px} y2={lineEnd.py}
        stroke={color} strokeWidth={1.5} strokeDasharray="5 4" opacity={0.9}
      />
      <ActionNode x={end.px} y={end.py} label={progression} color={color} />
    </g>
  );
});

/** Phase B.4a — the Threat teaching cue: "THIS defender is the primary
 * danger to THIS offensive player, right now."
 *
 * Phase B.4a.1 — REPLACED the original thick translucent halo (a filled
 * annulus spanning radius ~9.5-15, deliberately reusing PlaybookMarker's
 * B.3d-era reserved "future halo" zone) with a single thin CONCENTRIC
 * ring, per that pass's own explicit "too large/too heavy, does not read
 * as a layered marker state" feedback. The marker hierarchy is now
 * genuinely three concentric layers, all sized off the SAME geometry:
 *   1. player circle          — PlaybookMarker's own VISUAL_RADIUS
 *   2. selected-player ring   — PlaybookMarker's own SELECTION_RING_RADIUS
 *   3. THIS Threat ring       — SELECTION_RING_RADIUS + one more small gap
 * `THREAT_RING_GAP` intentionally reuses the SAME magnitude as
 * PlaybookMarker's own SELECTION_RING_GAP (1.5) rather than a fresh
 * magic number, so the gap-to-ring-to-gap rhythm feels like one visual
 * system, not two unrelated ones. The ring sits at a FIXED radius
 * regardless of whether the defender happens to be selected right now
 * (§"should still occupy the outer-ring position cleanly" when
 * unselected) — this deliberately avoids the ring's own radius jumping
 * around as selection state toggles; when the defender IS selected, the
 * existing selection ring simply appears one layer further in, with a
 * clean unmerged gap between the two. Plain stroke, transparent fill, no
 * opacity reduction, no animation — a thin solid outline reads as "one
 * more ring" the same unambiguous way PlaybookMarker's own selection
 * ring does, rather than as a soft glow/halo. */
const THREAT_RING_GAP = 1.5;
const THREAT_RING_RADIUS = SELECTION_RING_RADIUS + THREAT_RING_GAP;
const THREAT_RING_STROKE_WIDTH = 1.25; // matches PlaybookMarker's own selection-ring stroke width
const THREAT_COLOR = '#e0483a';
const ThreatHalo = React.memo(function ThreatHalo({ x, y }) {
  return (
    <circle
      cx={x} cy={y} r={THREAT_RING_RADIUS}
      fill="none" stroke={THREAT_COLOR}
      strokeWidth={THREAT_RING_STROKE_WIDTH}
    />
  );
});

/** A Zone assignment's area — a translucent blue circle, conventional
 * defensive-playbook visual language (not proprietary artwork). Fixed
 * default radius this pass (ZONE_DEFAULT_RADIUS_YD) — no resize handle. */
const ZoneArea = React.memo(function ZoneArea({ center, radiusPx }) {
  const meta = ASSIGNMENT_TYPE_META.zone;
  return (
    <circle
      cx={center.px} cy={center.py} r={radiusPx}
      fill={meta.color} fillOpacity={0.22} stroke={meta.color} strokeOpacity={0.55} strokeWidth={1}
    />
  );
});

/** The automatic Zone "run here" cue (zoneArrowScreenPoints() above) —
 * a thin dashed blue line with a small arrowhead landing at the zone's
 * own center, deliberately subtler than a committed path assignment's
 * line (this is a derived visual cue, not authored data) so it
 * complements the translucent area instead of competing with it. */
const ZoneArrow = React.memo(function ZoneArrow({ start, end }) {
  const meta = ASSIGNMENT_TYPE_META.zone;
  const angleDeg = (Math.atan2(end.py - start.py, end.px - start.px) * 180) / Math.PI;
  return (
    <g>
      <line
        x1={start.px} y1={start.py} x2={end.px} y2={end.py}
        stroke={meta.color} strokeWidth={1.25} strokeOpacity={0.7} strokeDasharray="3 2"
      />
      <polygon
        points="-3,-2.5 3,0 -3,2.5" fill={meta.color} fillOpacity={0.85}
        transform={`translate(${end.px},${end.py}) rotate(${angleDeg})`}
      />
    </g>
  );
});

export function PlayCard({ play, canWrite, onEdit, onDeleteClick }) {
  const ourName = play.document?.our?.source_name;
  const opponentName = play.document?.opponent?.source_name;
  const subtitle = [ourName, opponentName ? `vs ${opponentName}` : null].filter(Boolean).join(' ');
  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800 }}>{play.name}</Typography>
          {subtitle && (
            <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mt: 0.25 }}>{subtitle}</Typography>
          )}
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.25 }}>
            {formatPlayersPerSide(play.players_per_side)}
            {play.football_type && ` · ${FOOTBALL_TYPE_LABELS[play.football_type] || play.football_type}`}
          </Typography>
        </Box>
        {canWrite && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button size="small" onClick={() => onEdit(play)} sx={{ textTransform: 'none', fontWeight: 700 }}>Edit</Button>
            <Button size="small" color="error" onClick={() => onDeleteClick(play)} sx={{ textTransform: 'none', fontWeight: 700 }}>Delete</Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/** Create/Edit Play. play=null means create. In create mode, the coach
 * picks a starting Formation/Front/Unit (our side) and an Opponent Look
 * (a saved alignment from the opposite book, or a starter template) —
 * both get SNAPSHOTTED (deep-copied markers, fresh ids) into the Play's
 * own document; neither selector is shown again once the Play exists,
 * since re-selecting would mean starting over, not adjusting. */
/** Create/Edit Play. play=null means create. Phase B.2.2 — Formation-
 * first workflow: `presetFormation` (a Formation/Front/Unit row, create
 * mode only) means the coach already reached this editor via that
 * Formation's own "+ Create Play" action (see TeamPlaybookPage.js) —
 * the redundant "choose a starting Formation" selector is skipped
 * entirely and the snapshot happens immediately at mount, via the exact
 * same snapshotMarkers() mechanism handlePickOurFormation() below
 * already used for the old manual-selection flow (no second snapshot
 * implementation). Opponent Look remains available but is explicitly
 * OPTIONAL either way — see the "No opponent look yet" MenuItem below
 * and this phase's own report on why that required no schema/validation
 * change (a Play with empty opponent.markers was already valid). */
export function PlayEditorView({ teamId, unitType, team, play, presetFormation, onSaved, onCancel }) {
  const term = structureTermLabel(unitType);
  const opponentUnitType = opponentUnitTypeFor(unitType);
  const opponentTerm = structureTermLabel(opponentUnitType);

  const [name, setName] = useState(play?.name || '');
  const initialFormat = playersPerSideToChoice(
    play?.players_per_side ?? (presetFormation ? presetFormation.players_per_side : null) ?? team.players_per_side ?? null,
  );
  const [formatChoice, setFormatChoice] = useState(initialFormat.choice);
  const [formatOtherText, setFormatOtherText] = useState(initialFormat.otherText);
  const [footballType, setFootballType] = useState(
    play?.football_type || (presetFormation ? presetFormation.football_type : '') || team.football_type || '',
  );
  const [doc, setDoc] = useState(() => {
    // Phase B.3g — no migration step needed here: Passing Read cues live
    // on timeline.keyframes[].cues once again (see playbookTimeline.js's
    // own header note), read directly and tolerantly by
    // qbMomentPassingReads/visiblePassingReadCue below — a genuinely
    // legacy B.3e-shaped cue (missing only `route_progress`) loads as-is
    // and simply surfaces as "needs read point" rather than being
    // migrated anywhere. B.3f's own `post_snap.passing_reads` array is
    // still guaranteed to exist by ensurePostSnap() below (so a Play
    // saved during that window loads without crashing) but is otherwise
    // unread — see utils/playbook.js's own header note.
    const base = ensureWidePlayFieldConfig(ensureTimeline(ensurePreSnap(
      ensurePostSnap(play?.document || emptyPlayDocument(unitType)),
    )));
    if (!play && presetFormation) {
      return {
        ...base,
        our: { source_name: presetFormation.name, markers: snapshotMarkers(presetFormation.document?.markers, newMarkerId) },
      };
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Create-mode-only starting-alignment selection state. ourFormationId
  // starts pre-filled from presetFormation (used at save time exactly
  // like a manually-picked one would be) so handleSave's own
  // `sourceFormationId: ourFormationId || null` needs no special case.
  const [ourFormations, setOurFormations] = useState([]);
  const [opponentFormations, setOpponentFormations] = useState([]);
  const [selectorsLoading, setSelectorsLoading] = useState(!play);
  const [ourFormationId, setOurFormationId] = useState(presetFormation?.id || '');
  const [opponentKey, setOpponentKey] = useState('');

  useEffect(() => {
    if (play) return; // editing an existing Play never re-shows selectors
    // presetFormation already tells us the "our" side — no need to fetch
    // the full list just to populate a selector nobody will see.
    Promise.all([
      presetFormation ? Promise.resolve([]) : listPlaybookFormations(teamId, unitType),
      listPlaybookFormations(teamId, opponentUnitType),
    ])
      .then(([ours, opp]) => { setOurFormations(ours); setOpponentFormations(opp); })
      .catch(e => setError(e.message))
      .finally(() => setSelectorsLoading(false));
  }, [play, teamId, unitType, opponentUnitType, presetFormation]);

  const resolvedPlayersPerSide = resolvePlayersPerSide(formatChoice, formatOtherText);

  const chosenOurFormation = presetFormation || ourFormations.find(f => f.id === ourFormationId) || null;

  const handlePickOurFormation = (formationId) => {
    setOurFormationId(formationId);
    setOpponentKey(''); // format may have changed — force re-picking a compatible opponent
    const formation = ourFormations.find(f => f.id === formationId);
    if (!formation) return;
    const fmt = playersPerSideToChoice(formation.players_per_side);
    setFormatChoice(fmt.choice);
    setFormatOtherText(fmt.otherText);
    setFootballType(formation.football_type || '');
    setDoc(d => ({
      ...d,
      our: { source_name: formation.name, markers: snapshotMarkers(formation.document?.markers, newMarkerId) },
    }));
  };

  // Opponent options compatible with the currently-resolved format —
  // saved Formations/Fronts/Units from the opponent's own book, plus
  // (only at 11v11, matching the starter templates' own 11v11-only
  // scope) that book's starter templates. Format compatibility is
  // enforced here, at selection time — never a silent marker-count
  // change, and never a database-level cross-document check (matching
  // Formation's own "count vs. format" being frontend-only).
  const compatibleSavedOpponents = resolvedPlayersPerSide
    ? opponentFormations.filter(f => f.players_per_side === resolvedPlayersPerSide)
    : [];
  const opponentStarters = resolvedPlayersPerSide === 11 ? startersForUnitType(opponentUnitType) : [];

  const handlePickOpponent = (key) => {
    setOpponentKey(key);
    if (!key) { setDoc(d => ({ ...d, opponent: { ...d.opponent, source_name: null, markers: [] } })); return; }
    if (key.startsWith(OPPONENT_KEY_PREFIX_FORMATION)) {
      const formationId = key.slice(OPPONENT_KEY_PREFIX_FORMATION.length);
      const formation = opponentFormations.find(f => f.id === formationId);
      if (!formation) return;
      setDoc(d => ({
        ...d,
        opponent: {
          unit_type: opponentUnitType, source_name: formation.name,
          markers: snapshotMarkers(formation.document?.markers, newMarkerId),
        },
      }));
    } else if (key.startsWith(OPPONENT_KEY_PREFIX_STARTER)) {
      const templateKey = key.slice(OPPONENT_KEY_PREFIX_STARTER.length);
      const template = opponentStarters.find(t => t.key === templateKey);
      if (!template) return;
      setDoc(d => ({
        ...d,
        opponent: {
          unit_type: opponentUnitType, source_name: template.label,
          markers: snapshotMarkers(template.markers, newMarkerId),
        },
      }));
    }
  };

  // Every marker-set change also re-prunes assignments (see
  // pruneAssignments()'s own header note) AND timeline actor_state (§41
  // of the B.2 spec — pruneTimelineActors() mirrors the exact same
  // reasoning: a no-op today since nothing in the Play editor currently
  // removes a marker, but it means a future marker-removal feature can
  // never leave dangling actor_id/target_actor_id OR timeline state
  // behind, in one consistent cleanup pass rather than two.
  // Phase B.3: pre_snap.assignments gets the exact same prune treatment
  // as post_snap.assignments — pruneAssignments() is already generic
  // over "an assignments array + the doc to check actor/target
  // existence against" (it never assumed post_snap specifically), so
  // this is a straight reuse, not a new implementation.
  // Phase B.3b: pre_snap.set_positions gets the exact same prune
  // treatment as pre_snap.assignments/post_snap.assignments —
  // pruneAssignments() is generic over "an array of {actor_side,
  // actor_id, [target_side/target_actor_id]} + the doc to check
  // existence against"; a set_positions entry has no target fields at
  // all, so its own target-clearing step is simply always a no-op for
  // them (see utils/playbook.js's own header note) — a safe, unmodified
  // reuse, not a new implementation.
  const handleChangeOurMarkers = (markers) => setDoc(d => {
    const next = { ...d, our: { ...d.our, markers } };
    const existsFn = (side, id) => markersForSide(next, side).some(m => m.id === id);
    return {
      ...next,
      post_snap: {
        assignments: pruneAssignments(next.post_snap.assignments, next),
        // Phase B.3g — post_snap.passing_reads is itself legacy/unread
        // (see utils/playbook.js's own header note) — still pruned here
        // purely for hygiene on the off chance an old Play's inert data
        // referenced a since-deleted actor; pruneAssignments() is a safe,
        // unmodified reuse regardless of whether anything reads the array.
        passing_reads: pruneAssignments(next.post_snap.passing_reads, next),
      },
      pre_snap: {
        assignments: pruneAssignments(next.pre_snap.assignments, next),
        set_positions: pruneAssignments(next.pre_snap.set_positions, next),
      },
      // Phase B.3g — pruneMomentCues() gets the exact same defensive
      // treatment as pruneTimelineActors() right beside it: no current UI
      // removes a marker yet, but a Moment-owned Passing Read cue must
      // never dangle on a deleted actor the moment that becomes possible.
      timeline: pruneMomentCues(pruneTimelineActors(next.timeline, existsFn), existsFn),
    };
  });
  const handleChangeOpponentMarkers = (markers) => setDoc(d => {
    const next = { ...d, opponent: { ...d.opponent, markers } };
    const existsFn = (side, id) => markersForSide(next, side).some(m => m.id === id);
    return {
      ...next,
      post_snap: {
        assignments: pruneAssignments(next.post_snap.assignments, next),
        passing_reads: pruneAssignments(next.post_snap.passing_reads, next),
      },
      pre_snap: {
        assignments: pruneAssignments(next.pre_snap.assignments, next),
        set_positions: pruneAssignments(next.pre_snap.set_positions, next),
      },
      timeline: pruneMomentCues(pruneTimelineActors(next.timeline, existsFn), existsFn),
    };
  });
  const handleChangeAssignments = (assignments) => setDoc(d => ({ ...d, post_snap: { ...d.post_snap, assignments } }));
  // Both pre_snap sub-handlers must preserve the OTHER pre_snap array —
  // pre_snap has two independent sparse arrays now (assignments +
  // set_positions, see utils/playbook.js's own header note), so neither
  // handler may replace the whole `pre_snap` object wholesale.
  const handleChangePreSnap = (assignments) => setDoc(d => ({ ...d, pre_snap: { ...d.pre_snap, assignments } }));
  const handleChangePreSnapSetPositions = (set_positions) => setDoc(d => ({ ...d, pre_snap: { ...d.pre_snap, set_positions } }));
  const handleChangeTimeline = (timeline) => setDoc(d => ({ ...d, timeline }));

  const opponentSourceFormationId = opponentKey.startsWith(OPPONENT_KEY_PREFIX_FORMATION)
    ? opponentKey.slice(OPPONENT_KEY_PREFIX_FORMATION.length)
    : null;

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (resolvedPlayersPerSide === null) { setError('Choose the players-per-side format.'); return; }
    if (resolvedPlayersPerSide < 4 || resolvedPlayersPerSide > 15) { setError('Players per side must be between 4 and 15.'); return; }
    if (!play && doc.our.markers.length === 0) { setError('Choose a starting Formation.'); return; }

    setSaving(true);
    try {
      if (play) {
        await updatePlaybookPlay({
          playId: play.id, name: name.trim(), playersPerSide: resolvedPlayersPerSide,
          footballType: footballType || null, document: doc,
        });
      } else {
        await createPlaybookPlay({
          teamId, unitType, name: name.trim(), playersPerSide: resolvedPlayersPerSide,
          footballType: footballType || null,
          sourceFormationId: ourFormationId || null,
          opponentSourceFormationId,
          document: doc,
        });
      }
      await onSaved();
    } catch (e) {
      setError(e.message || 'Could not save this Play.');
    } finally {
      setSaving(false);
    }
  };

  if (selectorsLoading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  }

  // Phase B.2.4 — field-first UX. The whole editor (compact header
  // through the field workspace) shares ONE max-width so the header/
  // name field never look narrower than the widened field below them
  // (TeamPlaybookPage.js widens the surrounding TeamHubLayout Container
  // while this editor is open — see that file's own note) — and so the
  // field itself stays a coherent app-sized workspace rather than
  // stretching across an ultrawide monitor (§26). Save moved into this
  // compact header row (reachable without scrolling past the field);
  // Cancel stays below the workspace as the lower-emphasis "leave"
  // action — matches the target mockup's "← Playbook  Name  Save" /
  // "Cancel" split without touching Team navigation itself.
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800 }}>{play ? 'Edit Play' : 'New Play'}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" size="small" onClick={() => setSettingsOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
          >
            ⚙ Settings
          </Button>
          <Button
            variant="contained" color="primary" size="small" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 800, minHeight: 40, px: 3 }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save'}
          </Button>
        </Box>
      </Box>

      <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mb: 2 }}>
        {resolvedPlayersPerSide ? formatPlayersPerSide(resolvedPlayersPerSide) : 'No format selected yet'}
        {footballType && ` · ${FOOTBALL_TYPE_LABELS[footballType] || footballType}`}
      </Typography>

      <TextField
        fullWidth label="Play name" value={name} onChange={e => setName(e.target.value)} required
        placeholder="Power Right" sx={{ mb: 2 }}
        autoFocus={!play && Boolean(presetFormation)}
      />

      {!play && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {presetFormation ? (
            <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem' }}>
              Starting {term.toLowerCase()}: <strong>{presetFormation.name}</strong>
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel id="our-formation-label">Starting {term}</InputLabel>
              <Select
                labelId="our-formation-label" label={`Starting ${term}`} value={ourFormationId}
                onChange={e => handlePickOurFormation(e.target.value)}
              >
                {ourFormations.length === 0 && (
                  <MenuItem value="" disabled>No saved {term.toLowerCase()}s yet</MenuItem>
                )}
                {ourFormations.map(f => (
                  <MenuItem key={f.id} value={f.id}>{f.name} — {formatPlayersPerSide(f.players_per_side)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Opponent Look is explicitly OPTIONAL (§8 of the B.2.2
              Formation-first pass) — a Play with an empty opponent side
              was already fully valid (no source/schema change needed;
              see this phase's own report). "No opponent look yet" is
              always selectable, even once compatible options exist, so
              picking one is never forced. */}
          <FormControl size="small" fullWidth disabled={!chosenOurFormation}>
            <InputLabel id="opponent-look-label">Opponent Look (optional)</InputLabel>
            <Select
              labelId="opponent-look-label" label="Opponent Look (optional)" value={opponentKey}
              onChange={e => handlePickOpponent(e.target.value)}
            >
              <MenuItem value="">No opponent look yet</MenuItem>
              {compatibleSavedOpponents.length === 0 && opponentStarters.length === 0 && (
                <MenuItem value="__none_available" disabled>
                  {chosenOurFormation ? `No compatible ${opponentTerm.toLowerCase()} available` : `Choose a starting ${term.toLowerCase()} first`}
                </MenuItem>
              )}
              {compatibleSavedOpponents.map(f => (
                <MenuItem key={`f:${f.id}`} value={`${OPPONENT_KEY_PREFIX_FORMATION}${f.id}`}>{f.name}</MenuItem>
              ))}
              {opponentStarters.map(t => (
                <MenuItem key={`s:${t.key}`} value={`${OPPONENT_KEY_PREFIX_STARTER}${t.key}`}>{t.label} (starter)</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {(play || doc.our.markers.length > 0) && (
        <DualMarkerField
          document={doc} onChangeOur={handleChangeOurMarkers} onChangeOpponent={handleChangeOpponentMarkers}
          onChangeAssignments={handleChangeAssignments} onChangePreSnap={handleChangePreSnap}
          onChangePreSnapSetPositions={handleChangePreSnapSetPositions} onChangeTimeline={handleChangeTimeline}
          readOnly={false} ourUnitType={unitType}
        />
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Save lives in the compact header above (§8) — Cancel remains
          here as the lower-emphasis "leave without saving" action. */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
          Cancel
        </Button>
      </Box>

      <Dialog
        open={settingsOpen} onClose={() => setSettingsOpen(false)}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Play Settings</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 280 }}>
          <Box>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Players per side</Typography>
            <FormatPicker choice={formatChoice} otherText={formatOtherText} onChoiceChange={setFormatChoice} onOtherTextChange={setFormatOtherText} />
          </Box>
          <Box>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Football type (optional)</Typography>
            <FootballTypePicker value={footballType} onChange={setFootballType} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" color="primary" onClick={() => setSettingsOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

